import { useState, useMemo, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator,
  Plus,
  Minus,
  TrendingDown,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  X,
  Check,
  Package,
  Percent,
  DollarSign,
  Share2,
  Bookmark,
  UploadCloud,
  Loader2,
  Edit2,
  Trash2,
  FileText,
  UserCheck,
} from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useListCptCodes,
  useListPatients,
  useCreateBundle,
  useListMasterCptCodes,
  useSubmitProviderEstimate,
  useUpdateCostEstimateDetails,
  useGetProvider,
  useGetLocalities,
  API_BASE,
} from "@/lib/api-client-react";
import { navigatePostSaveBundle } from "@/lib/utils";
import {
  exportEstimateToCSV,
  exportEstimateToPDF,
} from "@/lib/estimateExporter";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { useToast } from "@/hooks/use-toast";

const COLORS = ["#2563EB", "#06B6D4", "#22C55E"];

interface LineItem {
  id: string;
  code: string;
  description: string;
  category?: string;       // Clinical Code Category (e.g. "Evaluation & Management", "Surgery")
  codeType?: string;       // Billing Code Type (e.g. "CPT", "PFS", "CLFS")
  subcategory?: string;
  quantity: number;
  standardPrice: number; // FS Base Rate (100% Fee Schedule Rate)
  fsAdjustment: number; // FS Adjustment ($) = standardPrice - unitPrice
  unitPrice: number; // Provider Rate ($)
  gfeAdjustment: number; // GFE Adjustment ($)
  patientCharges: number; // Patient Charges ($) = (unitPrice - gfeAdjustment) * quantity
  currentPct?: number; // % of FS
  providerId?: string;
  providerName?: string;
  providerNpi?: string;
  providerTaxId?: string;
}

export function computeLineItem(item: {
  id: string;
  code: string;
  description: string;
  category?: string;
  codeType?: string;
  subcategory?: string;
  quantity?: number;
  standardPrice?: number;
  unitPrice?: number;
  gfeAdjustment?: number;
  currentPct?: number;
  providerId?: string;
  providerName?: string;
}): LineItem {
  const qty = Math.max(1, item.quantity || 1);
  const provRate = Math.max(0, item.unitPrice || 0);
  const baseRate =
    item.standardPrice !== undefined && item.standardPrice >= 0
      ? item.standardPrice
      : provRate;
  const fsAdj = Math.max(0, baseRate - provRate);
  const gfeAdj = Math.max(0, item.gfeAdjustment || 0);
  const singlePatientCharge = Math.max(0, provRate - gfeAdj);
  const patientCharges = Math.round(singlePatientCharge * qty * 100) / 100;

  return {
    id: item.id,
    code: item.code,
    description: item.description,
    category: item.category || "General",
    codeType: item.codeType,
    subcategory: item.subcategory,
    quantity: qty,
    standardPrice: Math.round(baseRate * 100) / 100,
    fsAdjustment: Math.round(fsAdj * 100) / 100,
    unitPrice: Math.round(provRate * 100) / 100,
    gfeAdjustment: Math.round(gfeAdj * 100) / 100,
    patientCharges,
    currentPct: item.currentPct,
    providerId: item.providerId,
    providerName: item.providerName,
  };
}

export default function CostEstimator() {
  const [, setLocation] = useLocation();
  const [patient, setPatient] = useState("");
  const [lines, setLines] = useState<LineItem[]>([]);
  const [selectedCpt, setSelectedCpt] = useState("");
  const [cptOpen, setCptOpen] = useState(false);
  const [cptSearch, setCptSearch] = useState("");
  const [editingEstimateId, setEditingEstimateId] = useState<string | null>(
    null,
  );

  // Custom Items State
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [customNotes, setCustomNotes] = useState("");

  // Edit Line Item Modal State
  const [editingLine, setEditingLine] = useState<LineItem | null>(null);
  const [editLineModalOpen, setEditLineModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    description: "",
    category: "General",
    quantity: "1",
    unitPrice: "",
    gfeAdjustment: "0",
    gfePercentage: "0",
    patientCharges: "0",
    percentage: "100",
    standardPrice: 0,
  });

  const handleOpenEditLineModal = (line: LineItem) => {
    const basePrice =
      line.standardPrice ||
      (line.currentPct && line.currentPct !== 0
        ? Math.round((line.unitPrice / (line.currentPct / 100)) * 100) / 100
        : line.unitPrice);
    let initialPct = "100";
    if (line.currentPct !== undefined && line.currentPct !== null) {
      initialPct = line.currentPct.toString();
    } else if (basePrice > 0) {
      initialPct = Math.round((line.unitPrice / basePrice) * 100).toString();
    }

    const gfeAdj = line.gfeAdjustment || 0;
    const gfePct =
      line.unitPrice > 0
        ? (Math.round((gfeAdj / line.unitPrice) * 100 * 10) / 10).toString()
        : "0";
    const qty = line.quantity || 1;
    const singlePatient = Math.max(0, line.unitPrice - gfeAdj);
    const totalPatient = (singlePatient * qty).toFixed(2);

    setEditingLine(line);
    setEditForm({
      description: line.description,
      category: line.category || "General",
      quantity: qty.toString(),
      unitPrice: line.unitPrice.toString(),
      gfeAdjustment: gfeAdj.toString(),
      gfePercentage: gfePct,
      patientCharges: totalPatient,
      percentage: initialPct,
      standardPrice: basePrice,
    });
    setEditLineModalOpen(true);
  };

  const handleEditPercentageChange = (pctStr: string) => {
    const pct = parseFloat(pctStr);
    const base = editForm.standardPrice;
    const qty = Math.max(1, parseInt(editForm.quantity) || 1);
    if (!isNaN(pct) && base > 0) {
      const calculatedPriceStr = (base * (pct / 100)).toFixed(2);
      const calculatedPrice = parseFloat(calculatedPriceStr) || 0;
      const gfePct = parseFloat(editForm.gfePercentage) || 0;
      const calculatedGfe =
        gfePct > 0
          ? ((calculatedPrice * gfePct) / 100).toFixed(2)
          : editForm.gfeAdjustment;
      const gfeVal = parseFloat(calculatedGfe) || 0;
      const calculatedPatient = Math.max(0, (calculatedPrice - gfeVal) * qty).toFixed(2);
      setEditForm((prev) => ({
        ...prev,
        percentage: pctStr,
        unitPrice: calculatedPriceStr,
        gfeAdjustment: calculatedGfe,
        patientCharges: calculatedPatient,
      }));
    } else {
      setEditForm((prev) => ({ ...prev, percentage: pctStr }));
    }
  };

  const handleEditUnitPriceChange = (priceStr: string) => {
    const price = parseFloat(priceStr);
    const base = editForm.standardPrice;
    const qty = Math.max(1, parseInt(editForm.quantity) || 1);
    if (!isNaN(price) && base > 0) {
      const calculatedPct = Math.round((price / base) * 100).toString();
      const gfePct = parseFloat(editForm.gfePercentage) || 0;
      const calculatedGfe =
        gfePct > 0
          ? ((price * gfePct) / 100).toFixed(2)
          : editForm.gfeAdjustment;
      const gfeVal = parseFloat(calculatedGfe) || 0;
      const calculatedPatient = Math.max(0, (price - gfeVal) * qty).toFixed(2);
      setEditForm((prev) => ({
        ...prev,
        unitPrice: priceStr,
        percentage: calculatedPct,
        gfeAdjustment: calculatedGfe,
        patientCharges: calculatedPatient,
      }));
    } else if (!isNaN(price)) {
      const gfeVal = parseFloat(editForm.gfeAdjustment) || 0;
      const calculatedPatient = Math.max(0, (price - gfeVal) * qty).toFixed(2);
      setEditForm((prev) => ({
        ...prev,
        unitPrice: priceStr,
        patientCharges: calculatedPatient,
      }));
    } else {
      setEditForm((prev) => ({ ...prev, unitPrice: priceStr }));
    }
  };

  const handleEditGfeAdjustmentChange = (gfeStr: string) => {
    const gfeVal = parseFloat(gfeStr);
    const price = parseFloat(editForm.unitPrice) || 0;
    const qty = Math.max(1, parseInt(editForm.quantity) || 1);
    if (!isNaN(gfeVal) && price > 0) {
      const calculatedGfePct = (
        Math.round((gfeVal / price) * 100 * 10) / 10
      ).toString();
      const calculatedPatient = Math.max(0, (price - gfeVal) * qty).toFixed(2);
      setEditForm((prev) => ({
        ...prev,
        gfeAdjustment: gfeStr,
        gfePercentage: calculatedGfePct,
        patientCharges: calculatedPatient,
      }));
    } else {
      setEditForm((prev) => ({ ...prev, gfeAdjustment: gfeStr }));
    }
  };

  const handleEditGfePercentageChange = (gfePctStr: string) => {
    const gfePct = parseFloat(gfePctStr);
    const price = parseFloat(editForm.unitPrice) || 0;
    const qty = Math.max(1, parseInt(editForm.quantity) || 1);
    if (!isNaN(gfePct) && price > 0) {
      const calculatedGfeStr = ((price * gfePct) / 100).toFixed(2);
      const gfeVal = parseFloat(calculatedGfeStr) || 0;
      const calculatedPatient = Math.max(0, (price - gfeVal) * qty).toFixed(2);
      setEditForm((prev) => ({
        ...prev,
        gfePercentage: gfePctStr,
        gfeAdjustment: calculatedGfeStr,
        patientCharges: calculatedPatient,
      }));
    } else {
      setEditForm((prev) => ({ ...prev, gfePercentage: gfePctStr }));
    }
  };

  const handleEditPatientChargesChange = (patientStr: string) => {
    const targetPatient = parseFloat(patientStr);
    const price = parseFloat(editForm.unitPrice) || 0;
    const qty = Math.max(1, parseInt(editForm.quantity) || 1);
    if (!isNaN(targetPatient) && targetPatient >= 0) {
      const singleCharge = targetPatient / qty;
      if (singleCharge > price) {
        const newPriceStr = singleCharge.toFixed(2);
        const base = editForm.standardPrice;
        const newPctStr =
          base > 0
            ? Math.round((singleCharge / base) * 100).toString()
            : editForm.percentage;
        setEditForm((prev) => ({
          ...prev,
          patientCharges: patientStr,
          unitPrice: newPriceStr,
          percentage: newPctStr,
          gfeAdjustment: "0",
          gfePercentage: "0",
        }));
      } else {
        const newGfeVal = Math.max(0, price - singleCharge);
        const newGfeStr = newGfeVal.toFixed(2);
        const newGfePctStr =
          price > 0 ? ((newGfeVal / price) * 100).toFixed(1) : "0";
        setEditForm((prev) => ({
          ...prev,
          patientCharges: patientStr,
          gfeAdjustment: newGfeStr,
          gfePercentage: newGfePctStr,
        }));
      }
    } else {
      setEditForm((prev) => ({ ...prev, patientCharges: patientStr }));
    }
  };

  const handleSaveEditedLine = () => {
    if (!editingLine) return;
    const qty = Math.max(1, parseInt(editForm.quantity) || 1);
    const price = parseFloat(editForm.unitPrice);
    if (isNaN(price) || price < 0) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid price",
        variant: "destructive",
      });
      return;
    }
    const gfeAdj = Math.max(0, parseFloat(editForm.gfeAdjustment) || 0);
    const pct = parseFloat(editForm.percentage);

    const updatedItem = computeLineItem({
      id: editingLine.id,
      code: editingLine.code,
      description: editForm.description.trim() || editingLine.description,
      category: editForm.category || editingLine.category || "General",
      codeType: editingLine.codeType,
      subcategory: editingLine.subcategory,
      quantity: qty,
      standardPrice:
        editForm.standardPrice || editingLine.standardPrice || price,
      unitPrice: price,
      gfeAdjustment: gfeAdj,
      currentPct: !isNaN(pct) ? pct : editingLine.currentPct,
      providerId: editingLine.providerId,
      providerName: editingLine.providerName,
    });

    setLines((prev) =>
      prev.map((item) => (item.id === editingLine.id ? updatedItem : item)),
    );

    toast({
      title: "Service Updated",
      description: `Updated details for ${editingLine.code}`,
    });
    setEditLineModalOpen(false);
    setEditingLine(null);
  };

  // Thumbnail Image State
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const getImageUrl = (url?: string | null) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const baseUrl = API_BASE.replace(/\/v1.*$/, "");
    return `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const handleThumbnailUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("thumbnail", file);
      let token = localStorage.getItem("token") || "";
      if (!token) {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          if (u.token) token = u.token;
        } catch (err) {}
      }
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/upload/thumbnail`, {
        method: "POST",
        headers,
        body: formData,
      });
      const json = await res.json();
      if (json.url) {
        setThumbnailUrl(json.url);
      } else {
        throw new Error(json.error || "Upload failed");
      }
    } catch (err: any) {
      toast({
        title: "Upload Failed",
        description: err.message || "Failed to upload thumbnail",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [bundleForm, setBundleForm] = useState({
    name: "",
    description: "",
    category: "General",
    status: "active",
    discountPercent: "0",
    finalPrice: "",
  });

  const [estimateModalOpen, setEstimateModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalTab, setActionModalTab] = useState<
    "send" | "estimate" | "bundle"
  >("send");
  const [estimateForm, setEstimateForm] = useState({
    name: "",
    description: "",
    memberId: "",
    discountPercent: "0",
    finalPrice: "",
  });

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: providerCptCodesData } = useListCptCodes({ limit: 5000 });
  const providerCptCodes = (providerCptCodesData?.items || []).filter(
    (c: any) => c.status !== "inactive",
  );
  const { data: rawMasterCodesData } = useListMasterCptCodes({ limit: 5000 });
  const rawMasterCodes = rawMasterCodesData?.items || [];
  const { data: localitiesData = [] } = useGetLocalities();
  const { data: patientsDataObj } = useListPatients({ limit: 1000 });
  const patients = patientsDataObj?.items || [];
  const createBundle = useCreateBundle();
  const createCostEstimate = useSubmitProviderEstimate();
  const updateCostEstimate = useUpdateCostEstimateDetails();
  const { toast } = useToast();

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try {
        setUser(JSON.parse(u));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlMemberId =
      searchParams.get("memberId") || searchParams.get("patientId");
    if (urlMemberId) {
      setPatient(urlMemberId);
      setEstimateForm((prev) => ({ ...prev, memberId: urlMemberId }));
    }
  }, []);

  useEffect(() => {
    const rawEditData = sessionStorage.getItem("edit_estimate");
    if (rawEditData) {
      try {
        const editData = JSON.parse(rawEditData);
        if (editData.id || editData.estimateId) {
          setEditingEstimateId(String(editData.id || editData.estimateId));
        }
        if (editData.items && Array.isArray(editData.items) && editData.items.length > 0) {
          const loadedItems = editData.items.map((it: any) => {
            const rawCat = typeof it.category === 'object' ? it.category?.name : (it.codeCategory || it.category);
            const isInvalid = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
            return computeLineItem({
              ...it,
              category: (!isInvalid && rawCat) ? String(rawCat) : (it.codeCategory || "General"),
              codeType: it.codeType || (isInvalid ? String(rawCat) : undefined)
            });
          });
          setLines(loadedItems);
          if (editData.name) {
            setEstimateForm((prev) => ({ ...prev, name: editData.name }));
          }
          if (editData.memberId) {
            setPatient(editData.memberId);
            setEstimateForm((prev) => ({
              ...prev,
              memberId: editData.memberId,
            }));
          }
          if (editData.discount !== undefined && editData.discount !== null) {
            const discStr = Number(editData.discount).toString();
            setSummaryDiscountPercent(discStr);
            if (editData.finalAmount) {
              setSummaryFinalPrice(Number(editData.finalAmount).toFixed(2));
              setEstimateForm((prev) => ({
                ...prev,
                discountPercent: discStr,
                finalPrice: Number(editData.finalAmount).toFixed(2),
              }));
            }
          }
          toast({
            title: "Estimate Loaded for Editing",
            description: `Auto-filled ${editData.items.length} service item(s) ${editData.patientName ? `for ${editData.patientName}` : ""}.`,
          });
        }
      } catch (err) {
        console.error("Failed to parse edit_estimate from sessionStorage", err);
      } finally {
        sessionStorage.removeItem("edit_estimate");
      }
    }
  }, [toast]);

  useEffect(() => {
    if (patient && Array.isArray(patients) && patients.length > 0) {
      const match = patients.find(
        (p: any) =>
          String(p.userId) === String(patient) ||
          String(p.id) === String(patient) ||
          (p.email && p.email.toLowerCase() === String(patient).toLowerCase()),
      );
      if (match) {
        const validMemberId = match.userId || match.id;
        if (estimateForm.memberId !== validMemberId) {
          setEstimateForm((prev) => ({ ...prev, memberId: validMemberId }));
        }
      }
    }
  }, [patients, patient]);

  const isGroup = localStorage.getItem("practiceType") === "group";
  const [bundleScope, setBundleScope] = useState<"GROUP" | "PROVIDER">("GROUP");
  const [participatingProviderIds, setParticipatingProviderIds] = useState<
    string[]
  >([]);
  const { data: currentProviderData } = useGetProvider(user?.id || "", {
    query: { enabled: !!user },
  });
  const myLinkedProviders = currentProviderData?.linkedProviders || [];

  const effectiveMedicareRate = useMemo(() => {
    if (
      isGroup &&
      bundleScope === "PROVIDER" &&
      participatingProviderIds.length > 0
    ) {
      const selectedProviderId = String(participatingProviderIds[0]);
      const provider = myLinkedProviders.find(
        (p: any) => String(p.providerId || p.id) === selectedProviderId,
      );
      if (provider && provider.medicareCharge) {
        return (
          parseFloat(String(provider.medicareCharge).replace("%", "")) || 100
        );
      }
    }
    return parseFloat(user?.provider?.medicareCharge || "100") || 100;
  }, [isGroup, bundleScope, participatingProviderIds, myLinkedProviders, user]);

  const [selectedCodeType, setSelectedCodeType] = useState<string>("all");

  const normalizeStateCode = (st: string) => {
    if (!st) return "";
    const s = st.trim().toUpperCase();
    if (
      s === "NEW YORK" ||
      s === "NEW YORK STATE" ||
      s === "N.Y." ||
      s === "NY"
    )
      return "NY";
    if (s === "CALIFORNIA" || s === "CA") return "CA";
    if (s === "TEXAS" || s === "TX") return "TX";
    if (s === "FLORIDA" || s === "FL") return "FL";
    return s;
  };

  const effectiveUser = currentProviderData || user;

  const providerLocalities = useMemo(() => {
    const selectedLocalityKeys = new Set<string>();

    if (user?.addresses && Array.isArray(user.addresses)) {
      user.addresses.forEach((a: any) => {
        const ids = a.localityIds || (a.localityId ? [a.localityId] : []);
        if (Array.isArray(ids)) {
          ids.forEach((id: any) => {
            if (id) selectedLocalityKeys.add(String(id).trim());
          });
        }
      });
    }

    if (
      selectedLocalityKeys.size === 0 &&
      user?.localities &&
      Array.isArray(user.localities)
    ) {
      user.localities.forEach((l: any) => {
        if (typeof l === "string") {
          selectedLocalityKeys.add(l.trim());
        } else if (l && typeof l === "object") {
          if (l.id) selectedLocalityKeys.add(String(l.id).trim());
          if (l.localityNumber)
            selectedLocalityKeys.add(String(l.localityNumber).trim());
          if (l.name) selectedLocalityKeys.add(String(l.name).trim());
        }
      });
    }

    const locMap = new Map<string, any>();
    const targetKeys = Array.from(selectedLocalityKeys);

    if (Array.isArray(localitiesData) && localitiesData.length > 0) {
      if (targetKeys.length > 0) {
        localitiesData.forEach((loc: any) => {
          const locIdStr = String(loc.id).trim();
          const locNumStr = String(loc.localityNumber || "").trim();
          const locNameLower = String(loc.name || "")
            .trim()
            .toLowerCase();

          for (const key of targetKeys) {
            if (!key) continue;
            const keyLower = key.toLowerCase();
            if (
              locIdStr === key ||
              (locNumStr && locNumStr === key) ||
              locNameLower === keyLower
            ) {
              locMap.set(locIdStr, loc);
              break;
            }
          }
        });
      }

      // Fallback: If no specific practice localities are selected yet, filter by address state
      if (locMap.size === 0) {
        const stateKeys = new Set<string>();
        if (user?.addresses && Array.isArray(user.addresses)) {
          user.addresses.forEach((a: any) => {
            if (a.state) {
              const norm = normalizeStateCode(a.state);
              if (norm) stateKeys.add(norm);
            }
          });
        }
        if (stateKeys.size === 0) stateKeys.add("NY");

        localitiesData.forEach((loc: any) => {
          if (loc.state && stateKeys.has(normalizeStateCode(loc.state))) {
            locMap.set(String(loc.id).trim(), loc);
          }
        });
      }
    }

    return Array.from(locMap.values());
  }, [user, localitiesData]);

  const providerLocations = useMemo(() => {
    const addrList =
      effectiveUser?.addresses || effectiveUser?.user?.addresses || [];
    const locations: Array<{
      id: string;
      displayLabel: string;
      locality: any;
      localityId?: string;
    }> = [];

    if (Array.isArray(addrList) && addrList.length > 0) {
      addrList.forEach((addr: any, index: number) => {
        const street = [addr.buildingNo, addr.street1, addr.street2]
          .filter(Boolean)
          .join(" ");
        const cityStateZip = [addr.city, addr.state, addr.zipCode]
          .filter(Boolean)
          .join(", ");
        const fullAddress =
          [street, cityStateZip].filter(Boolean).join(" - ") ||
          `Location ${index + 1}`;
        const isPrimaryTag = addr.isPrimary ? " (Primary)" : "";
        const displayLabel = `${fullAddress}${isPrimaryTag}`;

        let targetLocality: any = null;
        const locIds =
          addr.localityIds || (addr.localityId ? [addr.localityId] : []);

        if (Array.isArray(localitiesData) && localitiesData.length > 0) {
          if (locIds.length > 0) {
            targetLocality = localitiesData.find((l: any) =>
              locIds.some(
                (id: any) =>
                  String(l.id) === String(id) ||
                  String(l.localityNumber) === String(id),
              ),
            );
          }
          if (!targetLocality && addr.state) {
            const normState = normalizeStateCode(addr.state);
            targetLocality = localitiesData.find(
              (l: any) => normalizeStateCode(l.state) === normState,
            );
          }
        }

        locations.push({
          id: addr.id || `location-${index}`,
          displayLabel,
          locality: targetLocality || providerLocalities[0] || null,
          localityId: targetLocality?.id || providerLocalities[0]?.id,
        });
      });
    }

    if (locations.length === 0 && providerLocalities.length > 0) {
      providerLocalities.forEach((loc: any) => {
        locations.push({
          id: String(loc.id),
          displayLabel: `${loc.name}${loc.state ? `, ${loc.state}` : ""}`,
          locality: loc,
          localityId: String(loc.id),
        });
      });
    }

    return locations;
  }, [effectiveUser, localitiesData, providerLocalities]);

  const [selectedLocationId, setSelectedLocationId] = useState<string>("all");

  useEffect(() => {
    if (providerLocations.length > 0) {
      const isAlreadySelectedValid = providerLocations.some(
        (l) => String(l.id) === String(selectedLocationId),
      );
      if (
        !selectedLocationId ||
        selectedLocationId === "all" ||
        !isAlreadySelectedValid
      ) {
        setSelectedLocationId(String(providerLocations[0].id));
      }
    }
  }, [providerLocations, selectedLocationId]);

  const selectedLocation = useMemo(() => {
    return (
      providerLocations.find(
        (l) => String(l.id) === String(selectedLocationId),
      ) ||
      providerLocations[0] ||
      null
    );
  }, [selectedLocationId, providerLocations]);

  const selectedLocality =
    selectedLocation?.locality || providerLocalities[0] || null;

  const getRateForSelectedLocality = (
    ratesObj: any,
    selectedLocObj: any,
    loc: any,
    codeTypeName: string,
    codeTypeId: string,
  ) => {
    if (!ratesObj || typeof ratesObj !== "object") return null;

    const getValueFromSubObj = (subObj: any): number | null => {
      if (!subObj || typeof subObj !== "object") {
        if (typeof subObj === "number") return subObj;
        if (typeof subObj === "string" && !isNaN(Number(subObj)))
          return Number(subObj);
        return null;
      }
      if (
        codeTypeName &&
        subObj[codeTypeName] !== undefined &&
        subObj[codeTypeName] !== null &&
        !isNaN(Number(subObj[codeTypeName]))
      ) {
        return Number(subObj[codeTypeName]);
      }
      if (
        codeTypeId &&
        subObj[codeTypeId] !== undefined &&
        subObj[codeTypeId] !== null &&
        !isNaN(Number(subObj[codeTypeId]))
      ) {
        return Number(subObj[codeTypeId]);
      }

      const keys = Object.keys(subObj);
      if (codeTypeName) {
        const matchedKey = keys.find(
          (k) => k.toUpperCase() === codeTypeName.toUpperCase(),
        );
        if (
          matchedKey &&
          subObj[matchedKey] !== undefined &&
          subObj[matchedKey] !== null &&
          !isNaN(Number(subObj[matchedKey]))
        ) {
          return Number(subObj[matchedKey]);
        }
      }
      if (
        subObj.default !== undefined &&
        subObj.default !== null &&
        !isNaN(Number(subObj.default))
      ) {
        return Number(subObj.default);
      }
      if (
        subObj.General !== undefined &&
        subObj.General !== null &&
        !isNaN(Number(subObj.General))
      ) {
        return Number(subObj.General);
      }

      for (const k of keys) {
        const val = Number(subObj[k]);
        if (
          !isNaN(val) &&
          typeof subObj[k] !== "object" &&
          subObj[k] !== null &&
          subObj[k] !== undefined
        )
          return val;
      }
      return null;
    };

    const possibleKeys: string[] = [];

    if (selectedLocObj) {
      if (selectedLocObj.id)
        possibleKeys.push(String(selectedLocObj.id).trim());
      if (selectedLocObj.raw?.id)
        possibleKeys.push(String(selectedLocObj.raw.id).trim());

      const locIdx = providerLocations.findIndex(
        (l) => String(l.id) === String(selectedLocObj.id),
      );
      if (locIdx !== -1) {
        possibleKeys.push(`loc_${locIdx}`);
        possibleKeys.push(`location-${locIdx}`);
      }

      if (selectedLocObj.displayLabel) {
        const addrPart = selectedLocObj.displayLabel.split(" - ")[0];
        if (addrPart) possibleKeys.push(String(addrPart).trim());
      }
      if (selectedLocObj.raw?.address1)
        possibleKeys.push(String(selectedLocObj.raw.address1).trim());
      if (selectedLocObj.raw?.street1)
        possibleKeys.push(String(selectedLocObj.raw.street1).trim());
    }

    if (loc) {
      if (loc.id) possibleKeys.push(String(loc.id).trim());
      if (loc.localityNumber)
        possibleKeys.push(String(loc.localityNumber).trim());
      if (loc.name && loc.state)
        possibleKeys.push(
          `${loc.name} (${loc.state}${loc.city ? " - " + loc.city : ""})`,
        );
      if (loc.name && loc.state)
        possibleKeys.push(`${loc.name} (${loc.state})`);
      if (loc.name) possibleKeys.push(String(loc.name).trim());
    }

    // STEP 1: Check if a location-specific percentage exists for this location
    for (const key of possibleKeys) {
      if (!key) continue;
      const targetRate = ratesObj[key];
      const val = getValueFromSubObj(targetRate);
      if (val !== null) return val;
    }

    for (const key of Object.keys(ratesObj)) {
      const targetRate = ratesObj[key];
      if (!targetRate || typeof targetRate !== "object") continue;

      const keyLower = key.toLowerCase();
      const matchFound = possibleKeys.some(
        (k) =>
          k.toLowerCase() === keyLower ||
          keyLower.includes(k.toLowerCase()) ||
          k.toLowerCase().includes(keyLower),
      );

      if (matchFound) {
        const val = getValueFromSubObj(targetRate);
        if (val !== null) return val;
      }
    }

    // STEP 2: Fallback to global default percentage rate in ratesObj
    const globalVal = getValueFromSubObj(ratesObj);
    if (globalVal !== null) return globalVal;

    return null;
  };

  const cptCodes = useMemo(() => {
    const pwGpci = selectedLocality?.pwGpci ?? 1.0;
    const peGpci = selectedLocality?.peGpci ?? 1.0;
    const mpGpci = selectedLocality?.mpGpci ?? 1.0;

    const masterMap = new Map<string, any>();
    rawMasterCodes.forEach((m: any) => masterMap.set(m.code, m));

    const allCodes: any[] = [];
    const processedCodes = new Set<string>();

    const codesSource = providerCptCodes || [];

    codesSource.forEach((item: any) => {
      const master = masterMap.get(item.code) || item;
      const codeTypeObj = item.codeType || master?.codeType;
      const codeTypeName =
        (typeof codeTypeObj === "string" ? codeTypeObj : codeTypeObj?.name) ||
        "PFS";
      const isCLFS = codeTypeName.toUpperCase() === "CLFS";

      const workRvu = master.workRvu ?? 0.0;
      const peRvu = master.peRvu ?? 0.0;
      const mpRvu = master.mpRvu ?? 0.0;
      const cf = master.conversionFactor || 33.29;

      let baseStandardPrice = master.standardPrice || 0;
      if (!isCLFS && (workRvu > 0 || peRvu > 0 || mpRvu > 0)) {
        const rvuGpciSum = workRvu * pwGpci + peRvu * peGpci + mpRvu * mpGpci;
        if (rvuGpciSum > 0) {
          baseStandardPrice = rvuGpciSum * cf;
        }
      }

      let codeTypePct = effectiveMedicareRate || 100;
      const ratesObj =
        effectiveUser?.provider?.categoryRates ||
        effectiveUser?.categoryRates ||
        user?.provider?.categoryRates ||
        user?.categoryRates;
      if (ratesObj && typeof ratesObj === "object") {
        const foundRate = getRateForSelectedLocality(
          ratesObj,
          selectedLocation,
          selectedLocality,
          codeTypeName,
          master.codeTypeId,
        );
        if (foundRate !== null && !isNaN(foundRate)) {
          codeTypePct = foundRate;
        }
      }

      let price = item.price;
      if (selectedLocality || price === undefined || price === null) {
        price = Math.round(baseStandardPrice * (codeTypePct / 100) * 100) / 100;
      }

      const masterCat = typeof master?.category === "object" ? master.category?.name : master?.category;
      const itemCat = typeof item?.category === "object" ? item.category?.name : item?.category;
      const isInvalidItemCat = itemCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(itemCat).trim().toUpperCase());
      const resolvedCategory = (!isInvalidItemCat && itemCat ? itemCat : masterCat) || masterCat || (!isInvalidItemCat && itemCat ? itemCat : "General");

      allCodes.push({
        ...master,
        ...item,
        id: item.id || master?.id || item.code,
        originalMasterId: master?.id || item.id,
        providerOverrideId: item.id,
        code: item.code,
        codeType: codeTypeObj || master?.codeType,
        codeTypeName,
        category: resolvedCategory,
        codeCategory: resolvedCategory,
        description: item.description || master?.description || master?.codeShortDesc || item.code,
        price,
        status: item.status || "active",
        currentPct: codeTypePct,
      });
      processedCodes.add(item.code);
    });

    return allCodes;
  }, [
    rawMasterCodes,
    providerCptCodes,
    effectiveMedicareRate,
    selectedLocality,
    user,
  ]);

  const providerCodeTypes = user?.provider?.codeTypes || user?.codeTypes || [];
  const selectedCodeTypeNames = useMemo(() => {
    if (!Array.isArray(providerCodeTypes) || providerCodeTypes.length === 0)
      return [];
    return providerCodeTypes
      .map((ct: any) => {
        if (!ct) return "";
        let nameStr = "";
        if (typeof ct === "string") {
          nameStr = ct;
          if (rawMasterCodes && Array.isArray(rawMasterCodes)) {
            const foundMaster = rawMasterCodes.find(
              (m: any) =>
                m.codeType?.id === ct ||
                m.codeTypeId === ct ||
                m.codeType?.name === ct,
            );
            if (foundMaster?.codeType?.name) {
              nameStr = foundMaster.codeType.name;
            }
          }
        } else if (typeof ct === "object" && ct !== null) {
          nameStr = ct.name || ct.code || ct.id || "";
        } else {
          nameStr = String(ct || "");
        }
        if (typeof nameStr !== "string") nameStr = String(nameStr || "");
        return nameStr.trim().toUpperCase();
      })
      .filter(Boolean);
  }, [providerCodeTypes, rawMasterCodes]);

  const availableCodeTypes = useMemo(() => {
    if (selectedCodeTypeNames.length > 0) {
      return selectedCodeTypeNames.map((x) => String(x));
    }
    const types = new Set<string>();
    (rawMasterCodes || []).forEach((m: any) => {
      let typeName = "";
      if (typeof m.codeType === "string") {
        typeName = m.codeType;
      } else if (typeof m.codeType === "object" && m.codeType !== null) {
        typeName = m.codeType.name || m.codeType.code || "";
      }
      if (typeName && typeof typeName === "string") {
        types.add(typeName);
      }
    });
    return Array.from(types).sort();
  }, [rawMasterCodes, selectedCodeTypeNames]);

  const activeCodes = useMemo(() => {
    return (cptCodes ?? []).filter((c: any) => {
      if (c.status !== "active") return false;

      const codeTypeName = (
        c.codeTypeName ||
        c.codeType?.name ||
        (typeof c.codeType === "string" ? c.codeType : "") ||
        ""
      )
        .trim()
        .toUpperCase();

      if (selectedCodeType && selectedCodeType !== "all") {
        if (codeTypeName !== selectedCodeType.toUpperCase()) return false;
      } else if (selectedCodeTypeNames.length > 0) {
        if (!selectedCodeTypeNames.includes(codeTypeName)) return false;
      }

      if (cptSearch) {
        const s = cptSearch.toLowerCase();
        return (
          c.code.toLowerCase().includes(s) ||
          (c.description || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [cptCodes, cptSearch, selectedCodeType, selectedCodeTypeNames]);

  const selectedCode = cptCodes?.find((c) => String(c.id) === selectedCpt);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setCptOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const totalFsBaseRate = useMemo(
    () =>
      lines.reduce((s, l) => s + (l.standardPrice || 0) * (l.quantity || 1), 0),
    [lines],
  );
  const totalFsAdjustment = useMemo(
    () =>
      lines.reduce((s, l) => s + (l.fsAdjustment || 0) * (l.quantity || 1), 0),
    [lines],
  );
  const totalProviderCharges = useMemo(
    () => lines.reduce((s, l) => s + (l.unitPrice || 0) * (l.quantity || 1), 0),
    [lines],
  );
  const totalGfeAdjustment = useMemo(
    () =>
      lines.reduce((s, l) => s + (l.gfeAdjustment || 0) * (l.quantity || 1), 0),
    [lines],
  );
  const totalPatientCharges = useMemo(
    () => lines.reduce((s, l) => s + (l.patientCharges || 0), 0),
    [lines],
  );

  const totalCost = totalPatientCharges;

  const [baseRateInput, setBaseRateInput] = useState<string | null>(null);
  const [fsAdjInput, setFsAdjInput] = useState<string | null>(null);
  const [provRateInput, setProvRateInput] = useState<string | null>(null);
  const [gfeAdjInput, setGfeAdjInput] = useState<string | null>(null);

  const handleEditTotalFsBaseRate = (val: number) => {
    if (lines.length === 0) return;
    const newTotal = Math.max(0, val);
    if (lines.length === 1) {
      const qty = lines[0].quantity || 1;
      const newBase = Math.round((newTotal / qty) * 100) / 100;
      setLines([computeLineItem({ ...lines[0], standardPrice: newBase })]);
      return;
    }
    const currentTotal = totalFsBaseRate;
    if (currentTotal > 0) {
      const ratio = newTotal / currentTotal;
      setLines((prev) =>
        prev.map((line) => {
          const newBase = Math.round(line.standardPrice * ratio * 100) / 100;
          return computeLineItem({ ...line, standardPrice: newBase });
        }),
      );
    } else {
      const perLine = Math.round((newTotal / lines.length) * 100) / 100;
      setLines((prev) =>
        prev.map((line) => {
          const qty = line.quantity || 1;
          return computeLineItem({
            ...line,
            standardPrice: Math.round((perLine / qty) * 100) / 100,
          });
        }),
      );
    }
  };

  const handleEditTotalFsAdjustment = (val: number) => {
    if (lines.length === 0) return;
    const newTotalAdj = Math.max(0, val);
    if (lines.length === 1) {
      const qty = lines[0].quantity || 1;
      const baseTotal = lines[0].standardPrice * qty;
      const newProvTotal = Math.max(0, baseTotal - newTotalAdj);
      const newUnitPrice = Math.round((newProvTotal / qty) * 100) / 100;
      setLines([computeLineItem({ ...lines[0], unitPrice: newUnitPrice })]);
      return;
    }
    const currentBaseTotal = totalFsBaseRate;
    setLines((prev) =>
      prev.map((line) => {
        const qty = line.quantity || 1;
        const lineBaseTotal = line.standardPrice * qty;
        const lineAdj =
          currentBaseTotal > 0
            ? (lineBaseTotal / currentBaseTotal) * newTotalAdj
            : newTotalAdj / lines.length;
        const newLineProvTotal = Math.max(0, lineBaseTotal - lineAdj);
        const newUnitPrice = Math.round((newLineProvTotal / qty) * 100) / 100;
        return computeLineItem({ ...line, unitPrice: newUnitPrice });
      }),
    );
  };

  const handleEditTotalProviderRate = (val: number) => {
    if (lines.length === 0) return;
    const newTotal = Math.max(0, val);
    if (lines.length === 1) {
      const qty = lines[0].quantity || 1;
      const newPrice = Math.round((newTotal / qty) * 100) / 100;
      setLines([computeLineItem({ ...lines[0], unitPrice: newPrice })]);
      return;
    }
    const currentTotal = totalProviderCharges;
    if (currentTotal > 0) {
      const ratio = newTotal / currentTotal;
      setLines((prev) =>
        prev.map((line) => {
          const newPrice = Math.round(line.unitPrice * ratio * 100) / 100;
          return computeLineItem({ ...line, unitPrice: newPrice });
        }),
      );
    } else {
      const perLine = Math.round((newTotal / lines.length) * 100) / 100;
      setLines((prev) =>
        prev.map((line) => {
          const qty = line.quantity || 1;
          return computeLineItem({
            ...line,
            unitPrice: Math.round((perLine / qty) * 100) / 100,
          });
        }),
      );
    }
  };

  const handleEditTotalGfeAdjustment = (val: number) => {
    if (lines.length === 0) return;
    const newTotal = Math.max(0, val);
    if (lines.length === 1) {
      const qty = lines[0].quantity || 1;
      const newGfe = Math.round((newTotal / qty) * 100) / 100;
      setLines([computeLineItem({ ...lines[0], gfeAdjustment: newGfe })]);
      return;
    }
    const currentTotal = totalGfeAdjustment;
    if (currentTotal > 0) {
      const ratio = newTotal / currentTotal;
      setLines((prev) =>
        prev.map((line) => {
          const newGfe =
            Math.round((line.gfeAdjustment || 0) * ratio * 100) / 100;
          return computeLineItem({ ...line, gfeAdjustment: newGfe });
        }),
      );
    } else {
      const currentProvTotal = totalProviderCharges;
      setLines((prev) =>
        prev.map((line) => {
          const qty = line.quantity || 1;
          const share =
            currentProvTotal > 0
              ? (line.unitPrice * qty) / currentProvTotal
              : 1 / lines.length;
          const newGfe = Math.round(((newTotal * share) / qty) * 100) / 100;
          return computeLineItem({ ...line, gfeAdjustment: newGfe });
        }),
      );
    }
  };

  const [totalPatientInput, setTotalPatientInput] = useState<string | null>(null);
  const [gfeAdjPercentInput, setGfeAdjPercentInput] = useState<string | null>(null);

  const handleEditTotalGfePercentage = (pct: number) => {
    if (lines.length === 0 || totalProviderCharges === 0) return;
    const targetVal =
      Math.round(totalProviderCharges * (Math.max(0, pct) / 100) * 100) / 100;
    handleEditTotalGfeAdjustment(targetVal);
  };

  const handleEditTotalPatientCharges = (val: number) => {
    if (lines.length === 0) return;
    const newTotalPatient = Math.max(0, val);
    const currentProvTotal = totalProviderCharges;

    if (newTotalPatient > currentProvTotal) {
      handleEditTotalProviderRate(newTotalPatient);
      setLines((prev) =>
        prev.map((line) => computeLineItem({ ...line, gfeAdjustment: 0 })),
      );
      return;
    }

    const targetTotalGfe = Math.max(0, currentProvTotal - newTotalPatient);
    handleEditTotalGfeAdjustment(targetTotalGfe);
  };

  const handlePatientChargeChange = (lineId: string, newTotalCharge: number) => {
    setLines((prev) =>
      prev.map((item) => {
        if (item.id !== lineId) return item;
        const qty = Math.max(1, item.quantity || 1);
        const targetCharge = Math.max(0, newTotalCharge);
        const singleCharge = targetCharge / qty;

        if (singleCharge > item.unitPrice) {
          const updatedRate = Math.round(singleCharge * 100) / 100;
          return computeLineItem({
            ...item,
            unitPrice: updatedRate,
            gfeAdjustment: 0,
          });
        } else {
          const newGfeAdj =
            Math.round((item.unitPrice - singleCharge) * 100) / 100;
          return computeLineItem({
            ...item,
            gfeAdjustment: newGfeAdj,
          });
        }
      }),
    );
  };

  const [summaryDiscountPercent, setSummaryDiscountPercent] =
    useState<string>("0");
  const [summaryFinalPrice, setSummaryFinalPrice] = useState<string>("");

  const handleSummaryDiscountChange = (val: string) => {
    setSummaryDiscountPercent(val);
    const discount = parseFloat(val) || 0;
    const final = totalCost - (totalCost * discount) / 100;
    setSummaryFinalPrice(val === "" || totalCost === 0 ? "" : final.toFixed(2));
  };

  const handleSummaryFinalPriceChange = (val: string) => {
    setSummaryFinalPrice(val);
    const final = parseFloat(val);
    if (!isNaN(final) && totalCost > 0) {
      const discount = ((totalCost - final) / totalCost) * 100;
      setSummaryDiscountPercent(discount.toFixed(1));
    } else {
      setSummaryDiscountPercent("0");
    }
  };

  const effectiveFinalPrice = totalPatientCharges;

  const effectiveDiscountNum = useMemo(() => {
    return totalProviderCharges > 0
      ? (totalGfeAdjustment / totalProviderCharges) * 100
      : 0;
  }, [totalProviderCharges, totalGfeAdjustment]);

  const addLine = () => {
    const code = cptCodes?.find((c) => String(c.id) === selectedCpt);
    if (!code) return;
    if (lines.some((l) => l.code === code.code)) return;

    const rawCat = typeof code.category === 'object' ? code.category?.name : (code.codeCategory || code.category);
    const isInvalidCat = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
    const clinicalCategory = (!isInvalidCat && rawCat) ? String(rawCat) : "General";
    const codeTypeName = (typeof code.codeType === 'object' ? code.codeType?.name : (code.codeTypeName || code.codeType)) || "CPT";
    const selectedProv = participatingProviderIds.length > 0
      ? myLinkedProviders.find((p: any) => String(p.providerId || p.id) === String(participatingProviderIds[0]))
      : null;

    const newItem = computeLineItem({
      id: Date.now().toString(),
      code: code.code,
      description: code.description,
      category: clinicalCategory,
      codeType: String(codeTypeName),
      subcategory: code.subcategory,
      quantity: 1,
      standardPrice: code.standardPrice || code.price,
      unitPrice: code.price,
      gfeAdjustment: 0,
      currentPct: code.currentPct,
      providerId: selectedProv
        ? String(selectedProv.providerId || selectedProv.id)
        : undefined,
      providerName: selectedProv ? selectedProv.name : undefined,
    });

    setLines((prev) => [...prev, newItem]);
    setSelectedCpt("");
  };

  const addCodeDirectly = (codeObj: any) => {
    if (!codeObj) return;
    if (lines.some((l) => l.code === codeObj.code)) {
      toast({
        title: "Item already added",
        description: `${codeObj.code} is already in this estimate.`,
      });
      return;
    }

    const rawCat = typeof codeObj.category === 'object' ? codeObj.category?.name : (codeObj.codeCategory || codeObj.category);
    const isInvalidCat = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
    const clinicalCategory = (!isInvalidCat && rawCat) ? String(rawCat) : "General";
    const codeTypeName = (typeof codeObj.codeType === 'object' ? codeObj.codeType?.name : (codeObj.codeTypeName || codeObj.codeType)) || "CPT";
    const selectedProv = participatingProviderIds.length > 0
      ? myLinkedProviders.find((p: any) => String(p.providerId || p.id) === String(participatingProviderIds[0]))
      : null;

    const newItem = computeLineItem({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 4),
      code: codeObj.code,
      description: codeObj.description,
      category: clinicalCategory,
      codeType: String(codeTypeName),
      subcategory: codeObj.subcategory,
      quantity: 1,
      standardPrice: codeObj.standardPrice || codeObj.price,
      unitPrice: codeObj.price,
      gfeAdjustment: 0,
      currentPct: codeObj.currentPct,
      providerId: selectedProv
        ? String(selectedProv.providerId || selectedProv.id)
        : undefined,
      providerName: selectedProv ? selectedProv.name : undefined,
    });

    setLines((prev) => [...prev, newItem]);
    setSelectedCpt("");
    setCptSearch("");
    setCptOpen(false);
    toast({
      title: "Billing Code Added",
      description: `${codeObj.code} (${codeObj.description}) added to estimate.`,
    });
  };

  const handleAddCustomItem = () => {
    if (!customName.trim())
      return toast({
        title: "Validation error",
        description: "Please enter custom item name",
        variant: "destructive",
      });
    const priceNum = parseFloat(customPrice);
    if (isNaN(priceNum) || priceNum <= 0)
      return toast({
        title: "Validation error",
        description: "Please enter a valid price",
        variant: "destructive",
      });

    const newItem = computeLineItem({
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      code: "CUSTOM",
      description:
        customName.trim() +
        (customNotes.trim() ? ` (${customNotes.trim()})` : ""),
      category: "Supplies / Custom",
      quantity: 1,
      standardPrice: priceNum,
      unitPrice: priceNum,
      gfeAdjustment: 0,
      currentPct: 0,
    });

    setLines((prev) => [...prev, newItem]);
    setCustomName("");
    setCustomPrice("");
    setCustomNotes("");
    toast({
      title: "Custom Item Added",
      description: `"${newItem.description}" added to estimate.`,
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (actionModalOpen || estimateModalOpen) return;
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA") &&
        activeEl.getAttribute("type") !== "button"
      ) {
        return;
      }
      if (e.key === "Enter" && selectedCpt) {
        e.preventDefault();
        addLine();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedCpt,
    lines,
    cptCodes,
    actionModalOpen,
    estimateModalOpen,
    participatingProviderIds,
    myLinkedProviders,
  ]);

  const handleExport = () => {
    if (lines.length === 0) return;
    let csv = "Service Code,Description,Price\n";
    lines.forEach((l) => {
      csv += `"${l.code}","${l.description}",$${l.unitPrice.toFixed(2)}\n`;
    });
    csv += `\nTotal Charges,,$${totalCost.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cost-estimate-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSaveBundle = async () => {
    if (!bundleForm.name || lines.length === 0) return;

    const cptCodeIds = lines
      .map((line) => {
        const code = cptCodes?.find((c) => c.code === line.code);
        return code?.id;
      })
      .filter((id) => id !== undefined) as string[];

    const customItems = lines
      .filter((l) => l.code === "CUSTOM")
      .map((l) => ({ id: l.id, name: l.description, price: l.unitPrice }));

    await createBundle.mutateAsync({
      data: {
        name: bundleForm.name,
        description: bundleForm.description,
        category: "General",
        status: bundleForm.status || "active",
        thumbnailUrl: thumbnailUrl || undefined,
        totalPrice: totalProviderCharges > 0 ? totalProviderCharges : totalCost,
        discountPercent:
          totalProviderCharges > 0
            ? Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) / 10
            : parseFloat(bundleForm.discountPercent) || 0,
        finalPrice:
          totalPatientCharges > 0
            ? totalPatientCharges
            : parseFloat(bundleForm.finalPrice) || totalCost,
        cptCodeIds: cptCodeIds,
        customItems: customItems,
        type:
          isGroup && bundleScope === "GROUP"
            ? "GROUP"
            : isGroup &&
                bundleScope === "PROVIDER" &&
                participatingProviderIds.length > 0
              ? `PROVIDER_${participatingProviderIds[0]}`
              : "INDIVIDUAL",
        participatingProviderIds: isGroup
          ? participatingProviderIds
          : undefined,
      },
    });

    toast({
      title: "Service Bundle Saved",
      description: `"${bundleForm.name}" has been created successfully.`,
    });
    setBundleModalOpen(false);
    setActionModalOpen(false);
    setLines([]);
    setPatient("");
    setBundleForm({
      name: "",
      description: "",
      category: "General",
      status: "active",
      discountPercent: "0",
      finalPrice: "",
    });
    setThumbnailUrl(null);
    navigatePostSaveBundle(setLocation);
  };

  const handleDiscountChange = (val: string) => {
    const discount = parseFloat(val) || 0;
    const final = totalCost - (totalCost * discount) / 100;
    setBundleForm((prev) => ({
      ...prev,
      discountPercent: val,
      finalPrice: final.toFixed(2),
    }));
  };

  const handleFinalPriceChange = (val: string) => {
    const final = parseFloat(val) || 0;
    const discount =
      totalCost > 0 ? ((totalCost - final) / totalCost) * 100 : 0;
    setBundleForm((prev) => ({
      ...prev,
      finalPrice: val,
      discountPercent: discount.toFixed(1),
    }));
  };

  const handleEstimateDiscountChange = (val: string) => {
    const discount = parseFloat(val) || 0;
    const final = totalCost - (totalCost * discount) / 100;
    setEstimateForm((prev) => ({
      ...prev,
      discountPercent: val,
      finalPrice: final.toFixed(2),
    }));
  };

  const handleEstimateFinalPriceChange = (val: string) => {
    const final = parseFloat(val) || 0;
    const discount =
      totalCost > 0 ? ((totalCost - final) / totalCost) * 100 : 0;
    setEstimateForm((prev) => ({
      ...prev,
      finalPrice: val,
      discountPercent: discount.toFixed(1),
    }));
  };

  const handleOpenActionModal = (tab: "send" | "estimate" | "bundle") => {
    setActionModalTab(tab);
    setEstimateForm({
      name: estimateForm.name || "Good Faith Estimate",
      description: estimateForm.description || "",
      memberId: patient || "",
      discountPercent: effectiveDiscountNum.toFixed(1),
      finalPrice: effectiveFinalPrice.toFixed(2),
    });
    setBundleForm({
      name: bundleForm.name || "Service Bundle",
      description: "Service bundle created from Good Faith Estimate",
      category: "General",
      status: "active",
      discountPercent: effectiveDiscountNum.toFixed(1),
      finalPrice: effectiveFinalPrice.toFixed(2),
    });
    setActionModalOpen(true);
  };

  const handleSaveActionModalSubmit = async () => {
    if (lines.length === 0) return;

    if (actionModalTab === "send") {
      const targetMemberId = estimateForm.memberId || patient || "";
      if (!targetMemberId) {
        toast({
          title: "Patient Required",
          description: "Please select a patient to send this estimate to.",
          variant: "destructive",
        });
        return;
      }

      const gfeDiscountPct =
        totalProviderCharges > 0
          ? Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) /
            10
          : 0;

      try {
        if (editingEstimateId) {
          await updateCostEstimate.mutateAsync({
            id: editingEstimateId,
            title: estimateForm.name || "Good Faith Estimate",
            name: estimateForm.name || "Good Faith Estimate",
            description: estimateForm.description || undefined,
            memberId: targetMemberId,
            totalCharges: totalProviderCharges,
            discount: gfeDiscountPct,
            finalAmount: totalPatientCharges,
            items: lines,
            status: "PENDING",
          });
          toast({
            title: "Estimate Updated & Sent",
            description: "Estimate has been updated and sent to the patient.",
          });
        } else {
          await createCostEstimate.mutateAsync({
            title: estimateForm.name || "Good Faith Estimate",
            name: estimateForm.name || "Good Faith Estimate",
            description: estimateForm.description || undefined,
            memberId: targetMemberId,
            totalCharges: totalProviderCharges,
            discount: gfeDiscountPct,
            finalAmount: totalPatientCharges,
            items: lines,
            status: "PENDING",
          });
          toast({
            title: "Estimate Sent to Patient",
            description:
              "Estimate is now pending patient review & price negotiation.",
          });
        }
        setActionModalOpen(false);
        setEditingEstimateId(null);
        setLines([]);
        setPatient("");
        setEstimateForm({
          name: "",
          description: "",
          memberId: "",
          discountPercent: "0",
          finalPrice: "",
        });
        setLocation("/provider/saved-estimates");
      } catch (err: any) {
        toast({
          title: "Error",
          description: err.message || "Failed to process estimate",
          variant: "destructive",
        });
      }
    } else if (actionModalTab === "estimate") {
      const gfeDiscountPct =
        totalProviderCharges > 0
          ? Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) /
            10
          : 0;

      try {
        if (editingEstimateId) {
          await updateCostEstimate.mutateAsync({
            id: editingEstimateId,
            title: estimateForm.name || "Good Faith Estimate",
            name: estimateForm.name || "Good Faith Estimate",
            description: estimateForm.description || undefined,
            memberId: undefined,
            totalCharges: totalProviderCharges,
            discount: gfeDiscountPct,
            finalAmount: totalPatientCharges,
            items: lines,
            status: "SAVED",
          });
          toast({
            title: "Estimate Updated",
            description: "Updated estimate in your library.",
          });
        } else {
          await createCostEstimate.mutateAsync({
            title: estimateForm.name || "Good Faith Estimate",
            name: estimateForm.name || "Good Faith Estimate",
            description: estimateForm.description || undefined,
            memberId: undefined,
            totalCharges: totalProviderCharges,
            discount: gfeDiscountPct,
            finalAmount: totalPatientCharges,
            items: lines,
            status: "SAVED",
          });
          toast({
            title: "Estimate Saved",
            description:
              "Saved to your estimate library for future patient use.",
          });
        }
        setActionModalOpen(false);
        setEditingEstimateId(null);
        setLines([]);
        setPatient("");
        setEstimateForm({
          name: "",
          description: "",
          memberId: "",
          discountPercent: "0",
          finalPrice: "",
        });
        setLocation("/provider/saved-estimates");
      } catch (err: any) {
        toast({
          title: "Error",
          description: err.message || "Failed to save estimate",
          variant: "destructive",
        });
      }
    } else if (actionModalTab === "bundle") {
      await handleSaveBundle();
    }
  };

  const removeLine = (id: string) =>
    setLines((prev) => prev.filter((l) => l.id !== id));

  const uniqueProviderIdsInLines = useMemo(() => {
    const ids = new Set<string>();
    lines.forEach((l) => {
      if (l.providerId) ids.add(l.providerId);
    });
    return Array.from(ids);
  }, [lines]);

  const patientObj = useMemo(() => {
    if (!patient || !Array.isArray(patients)) return null;
    return patients.find(
      (p: any) =>
        String(p.userId) === String(patient) ||
        String(p.id) === String(patient),
    );
  }, [patient, patients]);

  const pieData = lines.map((l) => ({
    name: l.code,
    value: Math.round(l.patientCharges || l.unitPrice),
  }));

  return (
    <ProviderLayout>
      <div className="w-full p-4 sm:p-6 lg:p-6 xl:p-8 flex flex-col min-h-[calc(100vh-8rem)]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-2.5 bg-primary/10 rounded-xl flex-shrink-0">
              <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight">
                Good Faith Estimate (GFE)
              </h1>
              <p className="text-muted-foreground mt-0.5 sm:mt-1 text-xs sm:text-sm">
                Build real-time cost breakdowns for patients instantly
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5">
            <Button
              variant="default"
              onClick={() => {
                const providerGroupsMap = new Map<string, any>();
                lines.forEach((l) => {
                  const pid = l.providerId || "main";
                  const foundLinked = myLinkedProviders.find(
                    (p: any) =>
                      String(p.id) === String(pid) ||
                      String(p.providerId) === String(pid),
                  );
                  const pname =
                    l.providerName ||
                    (foundLinked
                      ? foundLinked.name ||
                        `${foundLinked.user?.firstName || ""} ${foundLinked.user?.lastName || ""}`.trim()
                      : undefined) ||
                    effectiveUser?.provider?.name ||
                    effectiveUser?.name ||
                    user?.name ||
                    "Healthcare Provider Practice";
                  const pNpi =
                    l.providerNpi ||
                    (foundLinked
                      ? foundLinked.npiNumber || foundLinked.individualNpiNumber
                      : undefined) ||
                    effectiveUser?.provider?.npiNumber ||
                    effectiveUser?.provider?.groupNpiNumber ||
                    effectiveUser?.provider?.individualNpiNumber ||
                    effectiveUser?.npiNumber ||
                    user?.npiNumber ||
                    "1928374650";
                  const pTaxId =
                    l.providerTaxId ||
                    effectiveUser?.provider?.taxId ||
                    effectiveUser?.provider?.einNumber ||
                    user?.taxId ||
                    user?.einNumber ||
                    "XX-XXX4920";
                  const pAddress = [
                    effectiveUser?.addresses?.[0]?.buildingNo,
                    effectiveUser?.addresses?.[0]?.street1,
                    effectiveUser?.addresses?.[0]?.city,
                    effectiveUser?.addresses?.[0]?.state,
                    effectiveUser?.addresses?.[0]?.zipCode,
                  ]
                    .filter(Boolean)
                    .join(" ");

                  if (!providerGroupsMap.has(pid)) {
                    providerGroupsMap.set(pid, {
                      providerId: pid,
                      providerName: pname,
                      providerAddress: pAddress,
                      providerNpi: pNpi,
                      providerTaxId: pTaxId,
                      items: [],
                    });
                  }
                  providerGroupsMap.get(pid)!.items.push(l);
                });

                const providerGroups = Array.from(providerGroupsMap.values());

                exportEstimateToPDF({
                  title: estimateForm.name || "Good Faith Cost Estimate (GFE)",
                  patientName: patientObj?.name || "Patient / Member",
                  patientEmail: patientObj?.email || undefined,
                  patientMemberId:
                    patientObj?.userId ||
                    patientObj?.id ||
                    patient ||
                    undefined,
                  patientDob:
                    patientObj?.dob || patientObj?.dateOfBirth || undefined,
                  providerName:
                    effectiveUser?.provider?.name ||
                    effectiveUser?.name ||
                    user?.name ||
                    "Healthcare Provider Practice",
                  providerAddress: [
                    effectiveUser?.addresses?.[0]?.buildingNo,
                    effectiveUser?.addresses?.[0]?.street1,
                    effectiveUser?.addresses?.[0]?.city,
                    effectiveUser?.addresses?.[0]?.state,
                    effectiveUser?.addresses?.[0]?.zipCode,
                  ]
                    .filter(Boolean)
                    .join(" "),
                  providerNpi:
                    effectiveUser?.provider?.npiNumber ||
                    effectiveUser?.provider?.groupNpiNumber ||
                    effectiveUser?.provider?.individualNpiNumber ||
                    effectiveUser?.npiNumber ||
                    user?.npiNumber ||
                    "1928374650",
                  providerTaxId:
                    effectiveUser?.provider?.taxId ||
                    effectiveUser?.provider?.einNumber ||
                    user?.taxId ||
                    user?.einNumber ||
                    "XX-XXX4920",
                  items: lines,
                  providerGroups:
                    providerGroups.length > 1 ? providerGroups : undefined,
                  totalFsBaseRate,
                  totalFsAdjustment,
                  totalProviderCharges,
                  totalGfeAdjustment,
                  totalCharges: totalProviderCharges,
                  discount:
                    totalProviderCharges > 0
                      ? (totalGfeAdjustment / totalProviderCharges) * 100
                      : 0,
                  finalAmount: totalPatientCharges,
                });
              }}
              disabled={lines.length === 0}
              className="rounded-xl h-9 sm:h-10 px-4 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-sm w-full sm:w-auto justify-center"
              data-testid="button-export-pdf"
            >
              <FileText className="w-4 h-4" /> Print / Save Invoice PDF
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                exportEstimateToCSV({
                  title: estimateForm.name || "Good Faith Cost Estimate",
                  items: lines,
                  totalFsBaseRate,
                  totalFsAdjustment,
                  totalProviderCharges,
                  totalGfeAdjustment,
                  totalCharges: totalProviderCharges,
                  discount:
                    totalProviderCharges > 0
                      ? (totalGfeAdjustment / totalProviderCharges) * 100
                      : 0,
                  finalAmount: totalPatientCharges,
                });
              }}
              disabled={lines.length === 0}
              className="rounded-xl h-9 sm:h-10 px-4 text-xs font-bold border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 gap-2 shadow-2xs w-full sm:w-auto justify-center"
              data-testid="button-export-csv"
            >
              <Download className="w-4 h-4" /> Export Estimate CSV
            </Button>
          </div>
        </div>

        {/* Action Buttons on new line */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 mt-2 sm:mt-3 mb-5 sm:mb-6 w-full flex-wrap">
          {lines.length > 0 && (
            <>
              <Button
                variant="default"
                onClick={() => handleOpenActionModal("send")}
                className="rounded-xl gap-1.5 h-9 sm:h-10 px-3 sm:px-3.5 text-xs font-bold shadow-sm bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-initial"
              >
                <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> 1. Send to
                Patient
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleOpenActionModal("estimate")}
                className="rounded-xl gap-1.5 h-9 sm:h-10 px-3 sm:px-3.5 text-xs font-bold shadow-sm border border-slate-200 bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 flex-1 sm:flex-initial"
              >
                <Bookmark className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />{" "}
                2. Save as Estimate
              </Button>
              <Button
                variant="outline"
                onClick={() => handleOpenActionModal("bundle")}
                className="rounded-xl gap-1.5 h-9 sm:h-10 px-3 sm:px-3.5 text-xs font-bold shadow-sm border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 flex-1 sm:flex-initial"
              >
                <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" />{" "}
                3. Save as Bundle
              </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setLines([]);
              setPatient("");
              setEditingEstimateId(null);
              setBaseRateInput(null);
              setFsAdjInput(null);
              setProvRateInput(null);
              setGfeAdjInput(null);
            }}
            className="rounded-xl gap-1.5 h-9 sm:h-10 px-3 sm:px-3.5 text-xs font-bold shadow-sm hover:bg-slate-50 flex-1 sm:flex-initial"
            data-testid="button-reset"
          >
            <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Reset
          </Button>
        </div>

        <div className="flex-1 w-full flex flex-col">
          {/* Add Services & Table */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 sm:p-5 lg:p-6 flex-1 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-5 gap-3">
              <h2 className="text-lg sm:text-xl font-bold">Services</h2>

              {isGroup && (
                <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-stretch sm:items-center">
                  <div className="w-full sm:w-48 lg:w-52">
                    <Select
                      value={bundleScope}
                      onValueChange={(v: "GROUP" | "PROVIDER") => {
                        setBundleScope(v);
                        if (v === "GROUP") {
                          setParticipatingProviderIds(
                            myLinkedProviders.map(
                              (p: any) => p.providerId || p.id,
                            ),
                          );
                        } else {
                          setParticipatingProviderIds([]);
                        }
                      }}
                    >
                      <SelectTrigger className="h-9 sm:h-10 rounded-xl text-xs sm:text-sm">
                        <SelectValue placeholder="Estimation Scope" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GROUP">Group Estimation</SelectItem>
                        <SelectItem value="PROVIDER">
                          Provider Specific
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {bundleScope === "PROVIDER" && (
                    <div className="w-full sm:w-48 lg:w-52">
                      <Select
                        value={
                          participatingProviderIds[0]
                            ? String(participatingProviderIds[0])
                            : ""
                        }
                        onValueChange={(v) => setParticipatingProviderIds([v])}
                      >
                        <SelectTrigger className="h-9 sm:h-10 rounded-xl bg-white dark:bg-slate-950 text-xs sm:text-sm">
                          <SelectValue placeholder="Select a provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {myLinkedProviders.map((p: any) => (
                            <SelectItem
                              key={p.providerId || p.id}
                              value={String(p.providerId || p.id)}
                            >
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap xl:flex-nowrap gap-2.5 sm:gap-3 items-stretch sm:items-center mb-4 sm:mb-5">
              {/* Location Filter Dropdown */}
              <div className="w-full sm:w-[calc(50%-0.375rem)] xl:w-52 shrink-0">
                <Select
                  value={selectedLocationId}
                  onValueChange={(v) => setSelectedLocationId(v)}
                >
                  <SelectTrigger className="h-10 sm:h-11 rounded-xl bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 text-xs sm:text-sm hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {providerLocations.map((loc: any) => (
                      <SelectItem key={loc.id} value={String(loc.id)}>
                        {loc.displayLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* CPT Code Type Filter */}
              <div className="w-full sm:w-[calc(50%-0.375rem)] xl:w-40 shrink-0">
                <Select
                  value={selectedCodeType}
                  onValueChange={(v) => setSelectedCodeType(v)}
                >
                  <SelectTrigger className="h-10 sm:h-11 rounded-xl bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 text-xs sm:text-sm hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                    <SelectValue placeholder="All Code Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Code Types</SelectItem>
                    {availableCodeTypes.map((ct: string) => (
                      <SelectItem key={ct} value={ct}>
                        {ct}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Searchable CPT combobox */}
              <div className="flex-1 relative w-full min-w-0" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setCptOpen((v) => !v);
                    setCptSearch("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && selectedCpt && !cptOpen) {
                      e.preventDefault();
                      addLine();
                    }
                  }}
                  className={`w-full h-10 sm:h-11 px-3.5 rounded-xl border flex items-center justify-between gap-2 whitespace-nowrap transition-all ${
                    cptOpen
                      ? "border-primary ring-4 ring-primary/10 shadow-sm"
                      : "border-slate-200 dark:border-slate-800 hover:border-primary/40"
                  } bg-slate-50/50 dark:bg-slate-950/50`}
                  data-testid="select-cpt"
                >
                  {selectedCode ? (
                    <span className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                      <span className="font-mono font-bold text-primary text-xs flex-shrink-0 bg-primary/10 px-2 py-0.5 rounded">
                        {selectedCode.code}
                      </span>
                      <span className="text-muted-foreground truncate text-xs sm:text-sm font-medium">
                        {selectedCode.description}
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white flex-shrink-0 text-xs sm:text-sm">
                        ${selectedCode.price}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground flex items-center gap-2 font-medium text-xs sm:text-sm whitespace-nowrap min-w-0">
                      <Search className="w-4 h-4 shrink-0" />
                      <span className="truncate whitespace-nowrap">
                        Search CPT codes…
                      </span>
                    </span>
                  )}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {selectedCpt && (
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCpt("");
                          setCptSearch("");
                        }}
                        className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </span>
                    )}
                    <ChevronDown
                      className={`w-4 h-4 text-muted-foreground transition-transform ${cptOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>

                <AnimatePresence>
                  {cptOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.12 }}
                      className="absolute z-50 top-full mt-2 left-0 w-full min-w-[300px] max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl overflow-hidden"
                    >
                      {/* Search input */}
                      <div className="p-2.5 sm:p-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            autoFocus
                            value={cptSearch}
                            onChange={(e) => setCptSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (selectedCpt) {
                                  addLine();
                                  setCptOpen(false);
                                  setCptSearch("");
                                } else if (activeCodes.length > 0) {
                                  const firstAvailable =
                                    activeCodes.find(
                                      (c) =>
                                        !lines.some((l) => l.code === c.code),
                                    ) || activeCodes[0];
                                  if (firstAvailable) {
                                    setSelectedCpt(String(firstAvailable.id));
                                    addCodeDirectly(firstAvailable);
                                  }
                                }
                              }
                            }}
                            placeholder="Search by code or description… (Press Enter to add)"
                            className="pl-9 h-9 sm:h-10 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 focus-visible:ring-primary/20 text-xs sm:text-sm"
                          />
                        </div>
                      </div>

                      {/* Results list */}
                      <div className="max-h-64 sm:max-h-72 overflow-y-auto p-1.5 sm:p-2 space-y-1">
                        {activeCodes.length === 0 ? (
                          <div className="py-8 sm:py-10 text-center text-xs sm:text-sm text-slate-500">
                            <Search className="w-7 h-7 sm:w-8 sm:h-8 mx-auto mb-2.5 sm:mb-3 opacity-20" />
                            No matching codes found
                          </div>
                        ) : (
                          activeCodes.map((c) => {
                            const isSelected = String(c.id) === selectedCpt;
                            const isAdded = lines.some(
                              (l) => l.code === c.code,
                            );
                            const categoryLabel =
                              typeof c.category === "object" &&
                              c.category !== null
                                ? (c.category as any)?.name ||
                                  (c.category as any)?.code ||
                                  ""
                                : String(c.category || "");

                            return (
                              <button
                                key={c.id}
                                disabled={isAdded}
                                onClick={() => {
                                  setSelectedCpt(String(c.id));
                                  setCptOpen(false);
                                  setCptSearch("");
                                }}
                                className={`w-full text-left p-2.5 sm:p-3 rounded-xl transition-all group ${
                                  isAdded
                                    ? "opacity-50 cursor-not-allowed bg-slate-50/60 dark:bg-slate-900/40"
                                    : isSelected
                                      ? "bg-primary/10 border border-primary/25 shadow-2xs"
                                      : "hover:bg-slate-100/80 dark:hover:bg-slate-800/80 border border-transparent"
                                }`}
                              >
                                {/* Row 1: Code & Badges on Left, Price on Right */}
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                    <span
                                      className={`font-mono font-bold text-xs sm:text-sm px-2 py-0.5 rounded-md ${
                                        isSelected
                                          ? "bg-primary text-white"
                                          : "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors"
                                      }`}
                                    >
                                      {c.code}
                                    </span>
                                    {categoryLabel && (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] font-semibold h-5 px-1.5 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 max-w-[110px] sm:max-w-[140px] truncate"
                                      >
                                        {categoryLabel}
                                      </Badge>
                                    )}
                                    {c.currentPct !== undefined &&
                                      c.currentPct !== null && (
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px] font-semibold h-5 px-1.5 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                                        >
                                          {c.currentPct > 0 ? "+" : ""}
                                          {c.currentPct}%
                                        </Badge>
                                      )}
                                    {isAdded && (
                                      <Badge className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 border-0 text-[10px] font-bold h-5 px-1.5">
                                        Added
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0 text-right ml-auto">
                                    <span
                                      className={`text-sm sm:text-base font-extrabold tracking-tight ${
                                        isSelected
                                          ? "text-primary"
                                          : "text-slate-900 dark:text-white"
                                      }`}
                                    >
                                      ${(c.price || 0).toFixed(2)}
                                    </span>
                                    {isSelected && (
                                      <Check className="w-4 h-4 text-primary shrink-0" />
                                    )}
                                  </div>
                                </div>

                                {/* Row 2: Description on full width underneath */}
                                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                                  {c.description}
                                </p>
                              </button>
                            );
                          })
                        )}
                      </div>

                      {activeCodes.length > 0 && (
                        <div className="px-3.5 py-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80">
                          <p className="text-[11px] sm:text-xs font-medium text-slate-500 truncate">
                            {activeCodes.length} code
                            {activeCodes.length !== 1 ? "s" : ""} available —
                            press{" "}
                            <kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-800 rounded font-mono text-[10px]">
                              Enter
                            </kbd>{" "}
                            to add first match
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Button
                onClick={addLine}
                disabled={!selectedCpt}
                className="rounded-xl h-10 sm:h-11 px-5 sm:px-6 w-full sm:w-auto shrink-0 text-xs sm:text-sm font-bold shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 active:scale-[0.99] transition-all justify-center"
                data-testid="button-add-line"
              >
                Add to Estimate
              </Button>
            </div>

            {/* + ADD CUSTOM SERVICE / EXTRA ITEM */}
            <div className="mb-5 sm:mb-6 p-3.5 sm:p-4 rounded-2xl border border-amber-200/90 dark:border-amber-900/40 bg-gradient-to-r from-amber-50/50 via-amber-50/30 to-orange-50/20 dark:from-amber-950/25 dark:via-amber-950/15 dark:to-orange-950/10 space-y-2.5 sm:space-y-3 shadow-2xs">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-300">
                  + Add Custom Service / Item (e.g. Bandage, Extra Dressing,
                  Custom Supplies)
                </Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2 sm:gap-2.5">
                <Input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomItem();
                    }
                  }}
                  placeholder="Item / Service Name (e.g. Medical Bandage)"
                  className="sm:col-span-2 lg:col-span-4 h-9 sm:h-10 rounded-xl bg-white dark:bg-slate-900 border-amber-200/90 dark:border-amber-900/50 focus-visible:ring-2 focus-visible:ring-amber-500/20 focus-visible:border-amber-400 text-xs sm:text-sm shadow-2xs transition-all"
                />
                <Input
                  type="text"
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomItem();
                    }
                  }}
                  placeholder="Notes / Details (optional)"
                  className="sm:col-span-2 lg:col-span-4 h-9 sm:h-10 rounded-xl bg-white dark:bg-slate-900 border-amber-200/90 dark:border-amber-900/50 focus-visible:ring-2 focus-visible:ring-amber-500/20 focus-visible:border-amber-400 text-xs sm:text-sm shadow-2xs transition-all"
                />
                <Input
                  type="number"
                  step="0.01"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomItem();
                    }
                  }}
                  placeholder="Price ($)"
                  className="sm:col-span-1 lg:col-span-2 h-9 sm:h-10 rounded-xl bg-white dark:bg-slate-900 border-amber-200/90 dark:border-amber-900/50 focus-visible:ring-2 focus-visible:ring-amber-500/20 focus-visible:border-amber-400 text-xs sm:text-sm shadow-2xs transition-all"
                />
                <Button
                  type="button"
                  onClick={handleAddCustomItem}
                  className="sm:col-span-1 lg:col-span-2 h-9 sm:h-10 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold gap-1 w-full justify-center text-xs sm:text-sm shadow-sm shadow-amber-600/20 active:scale-[0.98] transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Item
                </Button>
              </div>
            </div>

            {/* Multi-Provider GFE Alert */}
            {isGroup && uniqueProviderIdsInLines.length > 1 && (
              <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-2xl flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-xl text-indigo-700 dark:text-indigo-300">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-indigo-900 dark:text-indigo-200">
                      Multi-Provider GFE Split Activated
                    </h4>
                    <p className="text-xs text-indigo-700 dark:text-indigo-300">
                      Services from {uniqueProviderIdsInLines.length} different
                      providers added. Independent GFEs will be issued
                      automatically with each provider's distinct NPI & header.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {lines.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-12 sm:py-16 text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/50 px-4 text-center">
                <Calculator className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 opacity-20" />
                <p className="text-base sm:text-lg font-medium text-slate-500">
                  No services added yet
                </p>
                <p className="text-xs sm:text-sm mt-1">
                  Search CPT codes or add custom items to build the estimate
                </p>
              </div>
            ) : (
              <div className="border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden shadow-2xs bg-white dark:bg-slate-950">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[1020px] border-collapse">
                    <thead className="bg-slate-50/90 dark:bg-slate-900/90 border-b border-slate-200/80 dark:border-slate-800 backdrop-blur-xs">
                      <tr>
                        <th className="text-left px-4 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Billing Code</th>
                        <th className="text-left px-4 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Description</th>
                        <th className="text-left px-3 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Code Category</th>
                        <th className="text-right px-3 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">FS Base Rate</th>
                        <th className="text-right px-3 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">FS Adjustment</th>
                        <th className="text-right px-3 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Provider Rate</th>
                        <th className="text-right px-3 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">GFE Adjustment</th>
                        <th className="text-right px-4 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Patient Charges</th>
                        <th className="text-center px-3 py-3.5 font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 bg-white dark:bg-slate-950">
                      <AnimatePresence>
                        {lines.map((line) => {
                          return (
                            <motion.tr
                              key={line.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, x: -10 }}
                              className="group hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-colors duration-150"
                            >
                              {/* 1. Billing Code */}
                              <td className="px-4 py-3.5 align-middle font-mono font-bold whitespace-nowrap">
                                <span className="bg-primary/10 text-primary px-2.5 py-1 rounded-md text-xs inline-block">
                                  {line.code}
                                </span>
                              </td>
                              {/* 2. Description */}
                              <td
                                className="px-4 py-3.5 align-middle font-medium text-slate-800 dark:text-slate-200 max-w-[240px] truncate"
                                title={line.description}
                              >
                                {line.description}
                              </td>
                              {/* 3. Code Category */}
                              <td className="px-3 py-3.5">
                                <Badge variant="secondary" className="text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 whitespace-nowrap">
                                  {line.category || "General"}
                                </Badge>
                              </td>
                              {/* 4. FS Base Rate */}
                              <td className="px-3 py-3.5 align-middle text-right font-semibold text-slate-500 whitespace-nowrap">
                                <div className="flex items-center justify-end">
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="FS Base Rate ($)"
                                  >
                                    <span className="text-[10px] text-slate-400 mr-0.5">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.standardPrice}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const newBase = isNaN(val) ? 0 : val;
                                        setLines((prev) =>
                                          prev.map((item) =>
                                            item.id === line.id
                                              ? computeLineItem({
                                                  ...item,
                                                  standardPrice: newBase,
                                                })
                                              : item,
                                          ),
                                        );
                                      }}
                                      className="w-18 h-7.5 text-right font-bold text-xs rounded-lg border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 focus-visible:ring-2 focus-visible:ring-primary/20 shadow-2xs transition-all"
                                    />
                                  </div>
                                </div>
                              </td>
                              {/* 5. FS Adjustment */}
                              <td className="px-3 py-3.5 align-middle text-right font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                                <div className="flex items-center justify-end">
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="FS Adjustment ($)"
                                  >
                                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 mr-0.5">
                                      -$
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.fsAdjustment}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const newAdj = isNaN(val) ? 0 : val;
                                        const newProvPrice = Math.max(
                                          0,
                                          line.standardPrice - newAdj,
                                        );
                                        setLines((prev) =>
                                          prev.map((item) =>
                                            item.id === line.id
                                              ? computeLineItem({
                                                  ...item,
                                                  unitPrice: newProvPrice,
                                                })
                                              : item,
                                          ),
                                        );
                                      }}
                                      className="w-18 h-7.5 text-right font-bold text-xs rounded-lg border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-1.5 focus-visible:ring-2 focus-visible:ring-emerald-500/20 shadow-2xs transition-all"
                                    />
                                  </div>
                                </div>
                              </td>
                              {/* 6. Provider Rate */}
                              <td className="px-3 py-3.5 align-middle text-right font-bold text-slate-900 dark:text-white whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1">
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="Provider Price ($)"
                                  >
                                    <span className="text-[10px] text-slate-400 mr-0.5">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.unitPrice}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const newPrice = isNaN(val) ? 0 : val;
                                        setLines((prev) =>
                                          prev.map((item) =>
                                            item.id === line.id
                                              ? computeLineItem({
                                                  ...item,
                                                  unitPrice: newPrice,
                                                })
                                              : item,
                                          ),
                                        );
                                      }}
                                      className="w-18 h-7.5 text-right font-bold text-xs rounded-lg border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 focus-visible:ring-2 focus-visible:ring-blue-500/20 shadow-2xs transition-all"
                                    />
                                  </div>
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="% of Fee Schedule Base Rate"
                                  >
                                    <Input
                                      type="number"
                                      step="1"
                                      min="0"
                                      max="500"
                                      value={
                                        line.standardPrice > 0
                                          ? Math.round(
                                              (line.unitPrice /
                                                line.standardPrice) *
                                                100,
                                            )
                                          : 100
                                      }
                                      onChange={(e) => {
                                        const pct = parseFloat(e.target.value);
                                        if (
                                          !isNaN(pct) &&
                                          line.standardPrice > 0
                                        ) {
                                          const newPrice =
                                            Math.round(
                                              line.standardPrice *
                                                (pct / 100) *
                                                100,
                                            ) / 100;
                                          setLines((prev) =>
                                            prev.map((item) =>
                                              item.id === line.id
                                                ? computeLineItem({
                                                    ...item,
                                                    unitPrice: newPrice,
                                                    currentPct: pct,
                                                  })
                                                : item,
                                            ),
                                          );
                                        }
                                      }}
                                      className="w-13 h-7.5 text-right font-semibold text-[11px] rounded-lg border-slate-200 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/70 text-slate-600 dark:text-slate-300 px-1 focus-visible:ring-1 transition-all"
                                    />
                                    <span className="text-[10px] font-bold text-slate-400 ml-0.5">
                                      %
                                    </span>
                                  </div>
                                </div>
                              </td>
                              {/* 7. GFE Adjustment */}
                              <td className="px-3 py-3.5 align-middle text-right font-bold text-purple-600 dark:text-purple-400 whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1">
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="GFE Adjustment ($)"
                                  >
                                    <span className="text-[10px] text-purple-400 mr-0.5">
                                      -$
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.gfeAdjustment || 0}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const newGfeAdj = isNaN(val) ? 0 : val;
                                        setLines((prev) =>
                                          prev.map((item) =>
                                            item.id === line.id
                                              ? computeLineItem({
                                                  ...item,
                                                  gfeAdjustment: newGfeAdj,
                                                })
                                              : item,
                                          ),
                                        );
                                      }}
                                      className="w-18 h-7.5 text-right font-bold text-xs rounded-lg border-purple-200 dark:border-purple-900/60 bg-purple-50/60 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 px-1.5 focus-visible:ring-2 focus-visible:ring-purple-500/20 shadow-2xs transition-all"
                                    />
                                  </div>
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="% Adjusted of Provider Rate"
                                  >
                                    <Input
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      max="100"
                                      value={
                                        line.unitPrice > 0
                                          ? Math.round(
                                              ((line.gfeAdjustment || 0) /
                                                line.unitPrice) *
                                                100 *
                                                10,
                                            ) / 10
                                          : 0
                                      }
                                      onChange={(e) => {
                                        const pct = parseFloat(e.target.value);
                                        if (!isNaN(pct) && line.unitPrice > 0) {
                                          const newGfeAdj =
                                            Math.round(
                                              line.unitPrice *
                                                (pct / 100) *
                                                100,
                                            ) / 100;
                                          setLines((prev) =>
                                            prev.map((item) =>
                                              item.id === line.id
                                                ? computeLineItem({
                                                    ...item,
                                                    gfeAdjustment: newGfeAdj,
                                                  })
                                                : item,
                                            ),
                                          );
                                        } else if (isNaN(pct) || pct === 0) {
                                          setLines((prev) =>
                                            prev.map((item) =>
                                              item.id === line.id
                                                ? computeLineItem({
                                                    ...item,
                                                    gfeAdjustment: 0,
                                                  })
                                                : item,
                                            ),
                                          );
                                        }
                                      }}
                                      className="w-13 h-7.5 text-right font-semibold text-[11px] rounded-lg border-purple-200 dark:border-purple-900/60 bg-purple-100/60 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 px-1 focus-visible:ring-1 transition-all"
                                    />
                                    <span className="text-[10px] font-bold text-purple-500 ml-0.5">
                                      %
                                    </span>
                                  </div>
                                </div>
                              </td>
                              {/* 8. Patient Charges */}
                              <td className="px-3 py-3.5 align-middle text-right font-black text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                                <div className="flex items-center justify-end">
                                  <div
                                    className="relative inline-flex items-center justify-end"
                                    title="Patient Charges / GFE ($) - Editable & Auto-Updating"
                                  >
                                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 mr-0.5 font-bold">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.patientCharges}
                                      onFocus={(e) => e.target.select()}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const newCharge = isNaN(val) ? 0 : val;
                                        handlePatientChargeChange(line.id, newCharge);
                                      }}
                                      className="w-20 h-7.5 text-right font-black text-xs rounded-lg border-emerald-300 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-1.5 focus-visible:ring-2 focus-visible:ring-emerald-500/20 shadow-2xs transition-all"
                                    />
                                  </div>
                                </div>
                              </td>
                              {/* Actions */}
                              <td className="px-3 py-3.5 align-middle text-center whitespace-nowrap">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleOpenEditLineModal(line)
                                    }
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all"
                                    title="Edit details"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeLine(line.id)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
                                    title="Remove item"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bottom Totals & Patient Responsibility Summary Card */}
            {lines.length > 0 && (
              <div className="mt-5 sm:mt-6 border-t border-slate-200 dark:border-slate-800 pt-5 sm:pt-6 space-y-3.5 sm:space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
                  <div className="bg-gradient-to-b from-slate-50 to-slate-100/50 dark:from-slate-900/80 dark:to-slate-900/40 p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-2xs min-w-0">
                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block truncate">
                      FS Base Rate
                    </span>
                    <div className="mt-1.5 flex items-center min-w-0">
                      <span className="text-sm font-extrabold text-slate-400 mr-1 shrink-0">
                        $
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          baseRateInput !== null
                            ? baseRateInput
                            : totalFsBaseRate.toFixed(2)
                        }
                        onFocus={(e) => {
                          setBaseRateInput(
                            totalFsBaseRate > 0
                              ? totalFsBaseRate.toFixed(2)
                              : "0",
                          );
                          e.target.select();
                        }}
                        onBlur={() => setBaseRateInput(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        onChange={(e) => {
                          const valStr = e.target.value;
                          setBaseRateInput(valStr);
                          const val = parseFloat(valStr);
                          if (!isNaN(val)) {
                            handleEditTotalFsBaseRate(val);
                          }
                        }}
                        className="h-8 sm:h-9 font-extrabold text-sm sm:text-base text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg px-2 w-full min-w-0 flex-1 shadow-2xs focus-visible:ring-2 focus-visible:ring-primary/20 transition-all"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-b from-emerald-50/60 to-emerald-50/20 dark:from-emerald-950/20 dark:to-slate-900/40 p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border border-emerald-200/80 dark:border-emerald-900/50 hover:border-emerald-300 dark:hover:border-emerald-800 transition-all shadow-2xs min-w-0">
                    <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block truncate">
                      FS Adjustment
                    </span>
                    <div className="mt-1.5 flex items-center min-w-0">
                      <span className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 mr-1 shrink-0">
                        -$
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          fsAdjInput !== null
                            ? fsAdjInput
                            : totalFsAdjustment.toFixed(2)
                        }
                        onFocus={(e) => {
                          setFsAdjInput(
                            totalFsAdjustment > 0
                              ? totalFsAdjustment.toFixed(2)
                              : "0",
                          );
                          e.target.select();
                        }}
                        onBlur={() => setFsAdjInput(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        onChange={(e) => {
                          const valStr = e.target.value;
                          setFsAdjInput(valStr);
                          const val = parseFloat(valStr);
                          if (!isNaN(val)) {
                            handleEditTotalFsAdjustment(val);
                          }
                        }}
                        className="h-8 sm:h-9 font-extrabold text-sm sm:text-base text-emerald-600 dark:text-emerald-400 bg-white dark:bg-slate-900 border-emerald-200 dark:border-emerald-900/50 rounded-lg px-2 w-full min-w-0 flex-1 shadow-2xs focus-visible:ring-2 focus-visible:ring-emerald-500/20 transition-all"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-b from-blue-50/60 to-blue-50/20 dark:from-blue-950/20 dark:to-slate-900/40 p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border border-blue-200/80 dark:border-blue-900/50 hover:border-blue-300 dark:hover:border-blue-800 transition-all shadow-2xs min-w-0">
                    <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block truncate">
                      Provider Rate
                    </span>
                    <div className="mt-1.5 flex items-center min-w-0">
                      <span className="text-sm font-extrabold text-blue-600 dark:text-blue-400 mr-1 shrink-0">
                        $
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          provRateInput !== null
                            ? provRateInput
                            : totalProviderCharges.toFixed(2)
                        }
                        onFocus={(e) => {
                          setProvRateInput(
                            totalProviderCharges > 0
                              ? totalProviderCharges.toFixed(2)
                              : "0",
                          );
                          e.target.select();
                        }}
                        onBlur={() => setProvRateInput(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        onChange={(e) => {
                          const valStr = e.target.value;
                          setProvRateInput(valStr);
                          const val = parseFloat(valStr);
                          if (!isNaN(val)) {
                            handleEditTotalProviderRate(val);
                          }
                        }}
                        className="h-8 sm:h-9 font-extrabold text-sm sm:text-base text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-900 border-blue-200 dark:border-blue-900/50 rounded-lg px-2 w-full min-w-0 flex-1 shadow-2xs focus-visible:ring-2 focus-visible:ring-blue-500/20 transition-all"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-b from-purple-50/60 to-purple-50/20 dark:from-purple-950/20 dark:to-slate-900/40 p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border border-purple-200/80 dark:border-purple-900/50 hover:border-purple-300 dark:hover:border-purple-800 transition-all shadow-2xs min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider block truncate">
                        GFE Adjustment
                      </span>
                      <span className="text-[9px] font-semibold text-purple-500/80">
                        ($ or %)
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 min-w-0">
                      <div className="relative flex items-center min-w-0 flex-1">
                        <span className="text-xs font-extrabold text-purple-600 dark:text-purple-400 mr-0.5 shrink-0">
                          -$
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={
                            gfeAdjInput !== null
                              ? gfeAdjInput
                              : totalGfeAdjustment.toFixed(2)
                          }
                          onFocus={(e) => {
                            setGfeAdjInput(
                              totalGfeAdjustment > 0
                                ? totalGfeAdjustment.toFixed(2)
                                : "0",
                            );
                            e.target.select();
                          }}
                          onBlur={() => setGfeAdjInput(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          onChange={(e) => {
                            const valStr = e.target.value;
                            setGfeAdjInput(valStr);
                            const val = parseFloat(valStr);
                            if (!isNaN(val)) {
                              handleEditTotalGfeAdjustment(val);
                            }
                          }}
                          className="h-8 sm:h-9 font-extrabold text-xs sm:text-sm text-purple-600 dark:text-purple-400 bg-white dark:bg-slate-900 border-purple-200 dark:border-purple-900/50 rounded-lg px-1.5 w-full min-w-0 flex-1 shadow-2xs focus-visible:ring-2 focus-visible:ring-purple-500/20 transition-all text-right"
                        />
                      </div>
                      <div className="relative flex items-center shrink-0 w-16 sm:w-20">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={
                            gfeAdjPercentInput !== null
                              ? gfeAdjPercentInput
                              : totalProviderCharges > 0
                                ? (
                                    Math.round(
                                      (totalGfeAdjustment / totalProviderCharges) *
                                        100 *
                                        10,
                                    ) / 10
                                  ).toString()
                                : "0"
                          }
                          onFocus={(e) => {
                            const currentPct =
                              totalProviderCharges > 0
                                ? (
                                    Math.round(
                                      (totalGfeAdjustment / totalProviderCharges) *
                                        100 *
                                        10,
                                    ) / 10
                                  ).toString()
                                : "0";
                            setGfeAdjPercentInput(currentPct);
                            e.target.select();
                          }}
                          onBlur={() => setGfeAdjPercentInput(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          onChange={(e) => {
                            const valStr = e.target.value;
                            setGfeAdjPercentInput(valStr);
                            const pct = parseFloat(valStr);
                            if (!isNaN(pct)) {
                              handleEditTotalGfePercentage(pct);
                            }
                          }}
                          className="h-8 sm:h-9 font-extrabold text-xs sm:text-sm text-purple-800 dark:text-purple-200 bg-purple-100/60 dark:bg-purple-900/40 border-purple-200 dark:border-purple-900/60 rounded-lg px-1 text-right w-full shadow-2xs focus-visible:ring-1 transition-all"
                        />
                        <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 ml-1">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50/90 via-sky-50/40 to-indigo-50/70 dark:from-slate-900 dark:via-blue-950/30 dark:to-indigo-950/30 rounded-2xl border-2 border-blue-200/80 dark:border-blue-900/60 p-4 sm:p-5 md:p-6 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <Badge
                      variant="outline"
                      className="bg-blue-600/10 text-blue-700 border-blue-200 dark:bg-blue-400/10 dark:text-blue-300 dark:border-blue-800 text-[10px] font-bold mb-1.5"
                    >
                      Patient Responsibility
                    </Badge>
                    <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                      Patient Responsibility (Amount Due)
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                      Total Patient Charges minus GFE Adjustment
                    </p>
                  </div>
                  <div className="w-full sm:w-auto flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-end pt-3 sm:pt-0 border-t border-blue-200/60 dark:border-slate-800 sm:border-0 text-left sm:text-right">
                    <div className="flex items-center min-w-0">
                      <span className="text-xl sm:text-2xl font-black text-blue-600 dark:text-blue-400 mr-1 shrink-0">
                        $
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                          totalPatientInput !== null
                            ? totalPatientInput
                            : totalPatientCharges.toFixed(2)
                        }
                        onFocus={(e) => {
                          setTotalPatientInput(
                            totalPatientCharges > 0
                              ? totalPatientCharges.toFixed(2)
                              : "0",
                          );
                          e.target.select();
                        }}
                        onBlur={() => setTotalPatientInput(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        onChange={(e) => {
                          const valStr = e.target.value;
                          setTotalPatientInput(valStr);
                          const val = parseFloat(valStr);
                          if (!isNaN(val)) {
                            handleEditTotalPatientCharges(val);
                          }
                        }}
                        className="h-9 sm:h-10 font-black text-lg sm:text-2xl text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-900 border-blue-300 dark:border-blue-800 rounded-xl px-2.5 w-32 sm:w-40 text-right shadow-2xs focus-visible:ring-2 focus-visible:ring-blue-500/20 transition-all"
                      />
                    </div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1">
                      Final Amount Due from Patient (Editable)
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={bundleModalOpen} onOpenChange={setBundleModalOpen}>
        <DialogContent className="rounded-2xl w-[95vw] sm:w-full max-w-[95vw] sm:max-w-[660px] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Save as Service Bundle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-500">
              Create a reusable bundle with the {lines.length} service
              {lines.length !== 1 ? "s" : ""} currently in your estimate.
            </p>

            {/* Thumbnail Image Upload */}
            <div>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Thumbnail Image
              </Label>
              <div className="flex items-center gap-4">
                {thumbnailUrl ? (
                  <div className="relative h-16 w-16 rounded-xl overflow-hidden border border-border group bg-muted/30">
                    <img
                      src={getImageUrl(thumbnailUrl)}
                      alt="Thumbnail preview"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setThumbnailUrl(null)}
                      className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove Image"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground bg-muted/20">
                    <Package className="h-6 w-6 opacity-40" />
                  </div>
                )}
                <div>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-white dark:bg-slate-900 text-xs font-semibold text-foreground cursor-pointer hover:bg-muted transition-colors shadow-2xs">
                    {uploadingImage ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                    ) : (
                      <UploadCloud className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {uploadingImage
                      ? "Uploading..."
                      : thumbnailUrl
                        ? "Change Image"
                        : "Upload Thumbnail"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleThumbnailUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    PNG, JPG, WEBP up to 5MB.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label>Bundle Name *</Label>
              <Input
                value={bundleForm.name}
                onChange={(e) =>
                  setBundleForm({ ...bundleForm, name: e.target.value })
                }
                placeholder="Custom Bundle Name"
                className="mt-1 rounded-xl"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={bundleForm.description}
                onChange={(e) =>
                  setBundleForm({ ...bundleForm, description: e.target.value })
                }
                placeholder="Created from cost estimator..."
                className="mt-1 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Location</Label>
                <Select
                  value={selectedLocationId}
                  onValueChange={(v) => setSelectedLocationId(v)}
                >
                  <SelectTrigger className="mt-1 rounded-xl bg-white dark:bg-slate-950">
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {providerLocations.map((loc: any) => (
                      <SelectItem key={loc.id} value={String(loc.id)}>
                        {loc.displayLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={bundleForm.status}
                  onValueChange={(v) =>
                    setBundleForm({ ...bundleForm, status: v })
                  }
                >
                  <SelectTrigger className="mt-1 rounded-xl bg-white dark:bg-slate-950">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isGroup && (
              <div className="space-y-3 pt-1">
                <div>
                  <Label>Bundle Type</Label>
                  <Select
                    value={bundleScope}
                    onValueChange={(v: "GROUP" | "PROVIDER") => {
                      setBundleScope(v);
                      if (v === "GROUP") {
                        setParticipatingProviderIds(
                          myLinkedProviders
                            .map((p: any) => String(p.providerId || p.id))
                            .filter(Boolean),
                        );
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GROUP">
                        Group Bundle (All Providers)
                      </SelectItem>
                      <SelectItem value="PROVIDER">
                        Provider Specific Bundle
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-foreground">
                      Select Included Providers
                    </Label>
                    <span className="text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      {participatingProviderIds.length} of{" "}
                      {myLinkedProviders.length} Selected
                    </span>
                  </div>

                  <div className="border border-border rounded-xl p-3 bg-muted/20 space-y-2 max-h-40 overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
                      <label className="flex items-center gap-2 text-xs font-bold text-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          checked={
                            participatingProviderIds.length ===
                              myLinkedProviders.length &&
                            myLinkedProviders.length > 0
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setParticipatingProviderIds(
                                myLinkedProviders
                                  .map((p: any) => String(p.providerId || p.id))
                                  .filter(Boolean),
                              );
                            } else {
                              setParticipatingProviderIds([]);
                            }
                          }}
                          className="rounded text-primary focus:ring-primary h-3.5 w-3.5"
                        />
                        Select All Providers
                      </label>
                    </div>
                    {myLinkedProviders.map((p: any) => {
                      const pId = String(p.providerId || p.id);
                      const isChecked = participatingProviderIds.includes(pId);
                      return (
                        <label
                          key={pId}
                          className="flex items-center gap-2 text-xs text-foreground cursor-pointer hover:text-primary transition-colors py-0.5"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setParticipatingProviderIds((prev) => [
                                  ...prev,
                                  pId,
                                ]);
                              } else {
                                setParticipatingProviderIds((prev) =>
                                  prev.filter((id) => id !== pId),
                                );
                              }
                            }}
                            className="rounded text-primary focus:ring-primary h-3.5 w-3.5"
                          />
                          <span className="font-medium">{p.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* GFE Adjustment & Patient Responsibility Amount Due */}
            <div className="p-3.5 rounded-xl bg-purple-50/60 dark:bg-purple-950/30 border border-purple-200/80 dark:border-purple-900/50 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-600 dark:text-slate-400">Total Patient Charges:</span>
                <span className="font-bold text-slate-900 dark:text-white">${totalProviderCharges.toFixed(2)}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">% Adjusted</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={
                        totalProviderCharges > 0
                          ? (Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) / 10).toString()
                          : bundleForm.discountPercent || "0"
                      }
                      onChange={(e) => {
                        const pct = parseFloat(e.target.value);
                        if (!isNaN(pct) && totalProviderCharges > 0) {
                          const targetVal = Math.round(totalProviderCharges * (pct / 100) * 100) / 100;
                          handleEditTotalGfeAdjustment(targetVal);
                        } else if (isNaN(pct) || pct === 0) {
                          handleEditTotalGfeAdjustment(0);
                        }
                        handleDiscountChange(e.target.value);
                      }}
                      className="pr-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">GFE Adjustment ($)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">-$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={totalGfeAdjustment}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const adj = isNaN(val) ? 0 : val;
                        handleEditTotalGfeAdjustment(adj);
                        const finalP = Math.max(0, totalProviderCharges - adj);
                        handleFinalPriceChange(finalP.toFixed(2));
                      }}
                      className="pl-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2.5 border-t border-purple-200 dark:border-purple-800 flex items-center justify-between">
                <div>
                  <span className="text-xs font-extrabold uppercase tracking-wider text-purple-700 dark:text-purple-300 block">
                    Patient Responsibility (Amount Due)
                  </span>
                </div>
                <span className="text-lg font-black text-purple-600 dark:text-purple-400">
                  ${totalPatientCharges.toFixed(2)}
                </span>
              </div>
            </div>

            <Button
              onClick={handleSaveBundle}
              className="w-full mt-4 rounded-xl h-11 font-semibold"
              disabled={!bundleForm.name || createBundle.isPending}
            >
              {createBundle.isPending ? "Saving..." : "Save Bundle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent className="rounded-2xl w-[95vw] sm:w-full max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Calculator className="w-5 h-5 text-primary" /> Create Action Item
              for GFE
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* 3 Action Tabs */}
            <div className="grid grid-cols-3 gap-1 sm:gap-2 bg-slate-100 dark:bg-slate-800 p-1 sm:p-1.5 rounded-2xl">
              <button
                type="button"
                onClick={() => setActionModalTab("send")}
                className={`py-2 px-1 sm:px-3 text-[11px] sm:text-xs font-bold rounded-xl transition-all flex flex-col items-center gap-1 ${
                  actionModalTab === "send"
                    ? "bg-white dark:bg-slate-900 text-blue-600 shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                }`}
              >
                <Share2 className="w-4 h-4" />
                1. Send to Patient
              </button>
              <button
                type="button"
                onClick={() => setActionModalTab("estimate")}
                className={`py-2 px-1 sm:px-3 text-[11px] sm:text-xs font-bold rounded-xl transition-all flex flex-col items-center gap-1 ${
                  actionModalTab === "estimate"
                    ? "bg-white dark:bg-slate-900 text-emerald-600 shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                }`}
              >
                <Bookmark className="w-4 h-4" />
                2. Save Estimate
              </button>
              <button
                type="button"
                onClick={() => setActionModalTab("bundle")}
                className={`py-2 px-1 sm:px-3 text-[11px] sm:text-xs font-bold rounded-xl transition-all flex flex-col items-center gap-1 ${
                  actionModalTab === "bundle"
                    ? "bg-white dark:bg-slate-900 text-purple-600 shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                }`}
              >
                <Package className="w-4 h-4" />
                3. Save as Bundle
              </button>
            </div>

            {/* Content for Action 1: Send to Patient */}
            {actionModalTab === "send" && (
              <div className="space-y-4 pt-1">
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl text-xs text-blue-800 dark:text-blue-300">
                  Sends this estimate to patient. Tracked as{" "}
                  <strong>PENDING</strong> for patient review & back-and-forth
                  price negotiation.
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200">
                    Select Patient *
                  </Label>
                  <Select
                    value={estimateForm.memberId}
                    onValueChange={(v) =>
                      setEstimateForm({ ...estimateForm, memberId: v })
                    }
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Choose a patient..." />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((p: any) => (
                        <SelectItem key={p.id} value={p.userId}>
                          {p.name} ({p.email || p.phone || "Patient"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200">
                    Estimate Title
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. Good Faith Estimate for Orthopedic Care"
                    value={estimateForm.name}
                    onChange={(e) =>
                      setEstimateForm({ ...estimateForm, name: e.target.value })
                    }
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200 text-xs flex items-center justify-between">
                    <span>Description</span>
                    <span className="text-slate-400 font-normal font-sans text-[11px]">
                      (Optional)
                    </span>
                  </Label>
                  <Textarea
                    placeholder="Add optional notes, details or clinical summary..."
                    value={estimateForm.description}
                    onChange={(e) =>
                      setEstimateForm({
                        ...estimateForm,
                        description: e.target.value,
                      })
                    }
                    className="mt-1 rounded-xl text-xs resize-none"
                    rows={2}
                  />
                </div>

                {/* GFE Adjustment & Patient Responsibility Amount Due */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-600 dark:text-slate-400">Total Patient Charges:</span>
                    <span className="font-bold text-slate-900 dark:text-white">${totalProviderCharges.toFixed(2)}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">% Adjusted</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={
                            totalProviderCharges > 0
                              ? (Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) / 10).toString()
                              : "0"
                          }
                          onChange={(e) => {
                            const pct = parseFloat(e.target.value);
                            if (!isNaN(pct) && totalProviderCharges > 0) {
                              const targetVal = Math.round(totalProviderCharges * (pct / 100) * 100) / 100;
                              handleEditTotalGfeAdjustment(targetVal);
                            } else if (isNaN(pct) || pct === 0) {
                              handleEditTotalGfeAdjustment(0);
                            }
                          }}
                          className="pr-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">GFE Adjustment ($)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">-$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={totalGfeAdjustment}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            handleEditTotalGfeAdjustment(isNaN(val) ? 0 : val);
                          }}
                          className="pl-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-2.5 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-extrabold uppercase tracking-wider text-blue-700 dark:text-blue-300 block">
                        Patient Responsibility (Amount Due)
                      </span>
                    </div>
                    <span className="text-lg font-black text-blue-600 dark:text-blue-400">
                      ${totalPatientCharges.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleSaveActionModalSubmit}
                  className="w-full mt-2 rounded-xl h-11 font-bold shadow-md bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={createCostEstimate.isPending}
                >
                  {createCostEstimate.isPending
                    ? "Sending..."
                    : "Send to Patient (Track as Pending)"}
                </Button>
              </div>
            )}

            {/* Content for Action 2: Save as Estimate */}
            {actionModalTab === "estimate" && (
              <div className="space-y-4 pt-1">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-800 dark:text-emerald-300">
                  Saves estimate template to your <strong>Estimates</strong>{" "}
                  library. Reusable in the future to send to new patients.
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200">
                    Estimate Name
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. Standard Knee Surgery Estimate"
                    value={estimateForm.name}
                    onChange={(e) =>
                      setEstimateForm({ ...estimateForm, name: e.target.value })
                    }
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200 text-xs flex items-center justify-between">
                    <span>Description</span>
                    <span className="text-slate-400 font-normal font-sans text-[11px]">
                      (Optional)
                    </span>
                  </Label>
                  <Textarea
                    placeholder="Add optional notes or description for future reuse..."
                    value={estimateForm.description}
                    onChange={(e) =>
                      setEstimateForm({
                        ...estimateForm,
                        description: e.target.value,
                      })
                    }
                    className="mt-1 rounded-xl text-xs resize-none"
                    rows={2}
                  />
                </div>

                {/* GFE Adjustment & Patient Responsibility Amount Due */}
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-600 dark:text-slate-400">Total Patient Charges:</span>
                    <span className="font-bold text-slate-900 dark:text-white">${totalProviderCharges.toFixed(2)}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">% Adjusted</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={
                            totalProviderCharges > 0
                              ? (Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) / 10).toString()
                              : "0"
                          }
                          onChange={(e) => {
                            const pct = parseFloat(e.target.value);
                            if (!isNaN(pct) && totalProviderCharges > 0) {
                              const targetVal = Math.round(totalProviderCharges * (pct / 100) * 100) / 100;
                              handleEditTotalGfeAdjustment(targetVal);
                            } else if (isNaN(pct) || pct === 0) {
                              handleEditTotalGfeAdjustment(0);
                            }
                          }}
                          className="pr-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">GFE Adjustment ($)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">-$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={totalGfeAdjustment}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            handleEditTotalGfeAdjustment(isNaN(val) ? 0 : val);
                          }}
                          className="pl-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-2.5 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 block">
                        Patient Responsibility (Amount Due)
                      </span>
                    </div>
                    <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">
                      ${totalPatientCharges.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleSaveActionModalSubmit}
                  className="w-full mt-2 rounded-xl h-11 font-bold shadow-md bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={createCostEstimate.isPending}
                >
                  {createCostEstimate.isPending
                    ? "Saving..."
                    : "Save to Estimates Library"}
                </Button>
              </div>
            )}

            {/* Content for Action 3: Save as Bundle */}
            {actionModalTab === "bundle" && (
              <div className="space-y-4 pt-1">
                <div className="p-3 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-xl text-xs text-purple-800 dark:text-purple-300">
                  Creates a public <strong>Service Bundle</strong> with Name and
                  Category to display on your public profile page.
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200">
                    Bundle Name *
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. Comprehensive Cardiac Screening Bundle"
                    value={bundleForm.name}
                    onChange={(e) =>
                      setBundleForm({ ...bundleForm, name: e.target.value })
                    }
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200">
                    Category *
                  </Label>
                  <Select
                    value={bundleForm.category}
                    onValueChange={(v) =>
                      setBundleForm({ ...bundleForm, category: v })
                    }
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Select Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Evaluation & Management">
                        Evaluation & Management
                      </SelectItem>
                      <SelectItem value="Surgery">Surgery</SelectItem>
                      <SelectItem value="Cardiology">Cardiology</SelectItem>
                      <SelectItem value="Radiology">Radiology</SelectItem>
                      <SelectItem value="Laboratory">Laboratory</SelectItem>
                      <SelectItem value="Physical Therapy">
                        Physical Therapy
                      </SelectItem>
                      <SelectItem value="Mental Health">
                        Mental Health
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="font-semibold text-slate-700 dark:text-slate-200">
                    Description
                  </Label>
                  <Input
                    type="text"
                    placeholder="Describe what is included in this bundle..."
                    value={bundleForm.description}
                    onChange={(e) =>
                      setBundleForm({
                        ...bundleForm,
                        description: e.target.value,
                      })
                    }
                    className="mt-1 rounded-xl"
                  />
                </div>

                {/* GFE Adjustment & Patient Responsibility Amount Due */}
                <div className="p-3.5 rounded-xl bg-purple-50/60 dark:bg-purple-950/30 border border-purple-200/80 dark:border-purple-900/50 space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-600 dark:text-slate-400">Total Patient Charges:</span>
                    <span className="font-bold text-slate-900 dark:text-white">${totalProviderCharges.toFixed(2)}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">% Adjusted</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={
                            totalProviderCharges > 0
                              ? (Math.round((totalGfeAdjustment / totalProviderCharges) * 100 * 10) / 10).toString()
                              : bundleForm.discountPercent || "0"
                          }
                          onChange={(e) => {
                            const pct = parseFloat(e.target.value);
                            if (!isNaN(pct) && totalProviderCharges > 0) {
                              const targetVal = Math.round(totalProviderCharges * (pct / 100) * 100) / 100;
                              handleEditTotalGfeAdjustment(targetVal);
                            } else if (isNaN(pct) || pct === 0) {
                              handleEditTotalGfeAdjustment(0);
                            }
                            handleDiscountChange(e.target.value);
                          }}
                          className="pr-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-purple-700 dark:text-purple-300 font-semibold">GFE Adjustment ($)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">-$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={totalGfeAdjustment}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            const adj = isNaN(val) ? 0 : val;
                            handleEditTotalGfeAdjustment(adj);
                            const finalP = Math.max(0, totalProviderCharges - adj);
                            handleFinalPriceChange(finalP.toFixed(2));
                          }}
                          className="pl-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-2.5 border-t border-purple-200 dark:border-purple-800 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-extrabold uppercase tracking-wider text-purple-700 dark:text-purple-300 block">
                        Patient Responsibility (Amount Due)
                      </span>
                    </div>
                    <span className="text-lg font-black text-purple-600 dark:text-purple-400">
                      ${totalPatientCharges.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleSaveActionModalSubmit}
                  className="w-full mt-2 rounded-xl h-11 font-bold shadow-md bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={!bundleForm.name || createBundle.isPending}
                >
                  {createBundle.isPending
                    ? "Publishing..."
                    : "Publish Public Service Bundle"}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Line Item Dialog */}
      <Dialog open={editLineModalOpen} onOpenChange={setEditLineModalOpen}>
        <DialogContent className="rounded-2xl w-[95vw] sm:w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" /> Edit Service Details
            </DialogTitle>
          </DialogHeader>
          {editingLine && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <Badge className="font-mono text-xs px-2 py-0.5">
                    {editingLine.code}
                  </Badge>
                  <span className="text-xs font-semibold truncate max-w-[140px] sm:max-w-[180px]">
                    {editingLine.description}
                  </span>
                </div>
                {editForm.standardPrice > 0 && (
                  <div className="text-right">
                    <span className="text-[10px] text-muted-foreground block font-medium">
                      Fee Schedule Base Rate
                    </span>
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      ${editForm.standardPrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <Label>Service Name / Description</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm({ ...editForm, description: e.target.value })
                  }
                  placeholder="Service description"
                  className="mt-1 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editForm.quantity}
                    onChange={(e) => {
                      const qtyStr = e.target.value;
                      const qty = Math.max(1, parseInt(qtyStr) || 1);
                      const price = parseFloat(editForm.unitPrice) || 0;
                      const gfeVal = parseFloat(editForm.gfeAdjustment) || 0;
                      const calculatedPatient = Math.max(0, (price - gfeVal) * qty).toFixed(2);
                      setEditForm({ ...editForm, quantity: qtyStr, patientCharges: calculatedPatient });
                    }}
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label>% of Fee Schedule</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="1"
                      value={editForm.percentage}
                      onChange={(e) =>
                        handleEditPercentageChange(e.target.value)
                      }
                      className="pr-7 rounded-xl font-bold"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <Label>Actual Price ($)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.unitPrice}
                    onChange={(e) => handleEditUnitPriceChange(e.target.value)}
                    className="pl-7 rounded-xl font-bold text-base text-primary"
                  />
                </div>
                {editForm.standardPrice > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1.5 leading-tight">
                    Adjusting <strong>% of Fee Schedule</strong> updates the{" "}
                    <strong>Actual Price</strong>, or typing an{" "}
                    <strong>Actual Price</strong> retro-calculates the{" "}
                    <strong>% of Fee Schedule</strong>.
                  </p>
                )}
              </div>

              {/* GFE Adjustment (%) and ($) Auto-Updating Section */}
              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-purple-900 dark:text-purple-300">
                  GFE Line Item Adjustment
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">
                      GFE Adjustment ($)
                    </Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">
                        -$
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.gfeAdjustment}
                        onChange={(e) =>
                          handleEditGfeAdjustmentChange(e.target.value)
                        }
                        className="pl-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[11px] text-muted-foreground">
                      % Adjusted
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={editForm.gfePercentage}
                        onChange={(e) =>
                          handleEditGfePercentageChange(e.target.value)
                        }
                        className="pr-7 rounded-xl font-bold text-purple-700 dark:text-purple-300"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-400">
                        %
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Editing either <strong>$</strong> or <strong>%</strong>{" "}
                  automatically updates the other and re-calculates Patient Responsibility (Amount Due).
                </p>
              </div>

              {/* Patient Responsibility (Amount Due) Section */}
              <div className="p-3.5 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-900/50 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                    Patient Responsibility (Amount Due)
                  </Label>
                  <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                    Auto-updating & Editable
                  </span>
                </div>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.patientCharges}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) =>
                      handleEditPatientChargesChange(e.target.value)
                    }
                    className="pl-7 rounded-xl font-black text-emerald-700 dark:text-emerald-300 text-base bg-white dark:bg-slate-900 border-emerald-300 dark:border-emerald-800 focus-visible:ring-emerald-500/20"
                  />
                </div>
                <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 leading-tight">
                  Editing this total patient charge directly recalculates the GFE Adjustment ($ and %).
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setEditLineModalOpen(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveEditedLine}
                  className="flex-1 rounded-xl font-bold"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
